/**
 * THE SHARED CARD — /pages/card?s={token}[&o={orderId}&n={orderNumber}]
 *
 * Plays somebody's finished card, from a link they were given. It is the same
 * eight CSS-3D planes the builder poses (CoverBack since v2e), driven by the
 * same card-stage.js, with none of the builder around them: no Alpine, no
 * dock, no editing, no IndexedDB.
 *
 * WHERE THE CARD COMES FROM. The pp-fulfilment Worker's public /s/{token} route
 * returns JSON built from the Shopify order — the baked Cloudinary panels, the
 * headline, the note, the captions. It never returns the address (see the long
 * note above handleShare in projects/pop-cards/fulfilment/worker.js), so there
 * is nothing on this page that a share link is not meant to hand out.
 *
 * ⛔ CALIBRATION IS NOT OPTIONAL. card-stage.js rasters each plane at its
 * LAYOUT size × DPR, and a fixed layout scale is what put 636MB on an iPhone
 * and got the 3D card pulled from the homepage on 2026-08-12. calibrate()
 * derives the scale per device against a 128MB budget — 31MB on a 375px phone
 * — and this page MUST call it before the first frame and again on resize.
 * Posing planes with a hardcoded scale would reintroduce that bug exactly.
 *
 * ⭐ YONATAN'S v2e RENDER (18 Oct; Daniel: "Move share page to V2e"). Every
 * motion on this page is one of his clips, the planes posed from the clip's
 * own video frame and its shading multiplied over them from a canvas:
 * card-open for the arrival and for Open, card-close for Closed, card-back
 * and card-back-rev to turn the card over and back. At rest the shading is a
 * still inside the camera (.pp-shade): the open card's, the shut card's or
 * the turned card's. See THE CLIPS and moveTo() below.
 *
 * ⭐ THE PAGE OPENS ON THE SPIN (18 Oct; Daniel: "Default should be the spin,
 * then closed, open, and back"): the recipient's own card, open, floating and
 * turning in Yonatan's 4 s loop. Into or out of it is a fade; Closed, Open and
 * Back play the clips between each other. See THE SPIN below.
 *
 * ⚠️ FRAME 100 IS CLOSED. The main geometry counts DOWN: 100 folded, front
 * up, the camera straight down on the cover; 0 fully open. The turn over is
 * its own geometry (card-geometry-back.json), 0 front up to 59 back up.
 */
(function () {
  var PANELS = ['OutLeft1', 'InLeft1', 'InRight1', 'OutRight1'];
  var FRAME_CLOSED = 100;
  var FRAME_OPEN = 0;
  var FPS = 30;
  /* Back to the builder's 0.94 now the stage fills the screen rather than
     being cropped to the card: the space is there to be used, and a card that
     leaves 35px of ground either side on a 390px phone reads as small. */
  var PAD = 0.94;
  /* The longest the closed card waits for its pictures and its cover before it
     opens anyway; whatever is late lands in the open card (see the boot). */
  var COVER_HOLD = 5000;
  var COMP_SPAN = 2000;        /* the AE comp, as in configurator.js */
  var SHADE_CANVAS_MAX = 3072; /* a MEMORY cap: 3072^2 x 4 ~= 37MB */
  /* ⛔ THE BUILDER'S PACING, VERBATIM — MIN 1.15, MAX 1.60, EASED BY u³.
     Daniel, 2026-09-10: "when it opens, it opens very slowly. I want basically
     the same thing that we see in the preview — the same fluid motion, the
     same speed of opening."

     This page had its own curve: a half-sine from 0.58 to 1.42, which averages
     about 1.0 and therefore played the artist's 100 frames at real time —
     3.33s. The builder runs the SAME frames at 1.15x to 1.60x, fastest at the
     first move and settling as the last panel lands, which is nearer 2.2s and
     is what everybody has actually seen the card do.

     ⚠️ The shape matters as much as the numbers. A sine is slow at BOTH ends,
     so the old curve crawled out of the fold — the worst possible place, since
     that first move is what tells you the thing is opening at all. The cubic
     leaves fast and decelerates once, like a hand letting go of a hinge.
     Keep in step with OPEN_RATE_* in configurator.js. */
  var OPEN_RATE_MIN = 1.15, OPEN_RATE_MAX = 1.60;

  /* ⭐ AND FASTER STILL ON A PHONE. Daniel, 2026-09-10: desktop "is good", and
     "the opening of the card is too slow" on mobile. The same animation reads
     differently on the two: on a desktop the card is one column of a page you
     are settling into, and on a phone it is the whole screen and the only
     thing between a tap on a link and what somebody was sent.

     ⚠️ A MULTIPLIER, NOT A SECOND CURVE. The shape — fast out of the fold,
     settling as the last panel lands — is the builder's and is not in
     question; only the tempo is. 1.35x takes the unfold from 2.27s to about
     1.7s, and the beats before it come down with it. */
  var PHONE = window.matchMedia && window.matchMedia('(hover: none)').matches;
  var RATE_BOOST = PHONE ? 1.35 : 1;

  function openRate(idx) {
    var u = idx / 100;
    return (OPEN_RATE_MAX - (OPEN_RATE_MAX - OPEN_RATE_MIN) * u * u * u) * RATE_BOOST;
  }

  /* How long to wait for the shading clip before opening WITHOUT it.
     ⚠️ 4000ms until 2026-09-10, and that is the "card stays closed on the page
     for a long time" Daniel reported. The clip is ~550KB with preload="auto",
     so it is fetching in parallel with the card JSON and the four photographs
     — which this page already waits for — and by that point it is usually
     there. When it is not, an unshaded opening that starts NOW beats a shaded
     one that starts in four seconds: the shading is a refinement, the opening
     is the entire reason anybody followed the link. */
  var SHADE_WAIT_MS = 900;

  var cfg = window.PP_SHARE || {};
  var root = document.querySelector('[data-pp-share]');
  if (!root) return;

  var stage = root.querySelector('[data-pp-stage]');
  var zoom = root.querySelector('[data-pp-camera]');
  var statusEl = root.querySelector('[data-pp-status]');

  var vid = root.querySelector('[data-pp-shadevid]');
  var cvs = root.querySelector('[data-pp-shadecanvas]');

  var geom = null, card = null, openBox = null, panelBox = null, cardBox = null, raf = 0;
  /* The camera the opening is frozen at, kept so the canvas can be placed on
     the same numbers rather than re-deriving them. */
  var camX = 0, camY = 0, camScale = 1;

  /* ── WHAT THE PAGE AROUND THE CARD HEARS (2026-10-10) ───────────────────
     The share page's direction 1 (sections/share-page.liquid) dresses the card
     from outside this file: its title from the card's names or occasion, the
     docked buttons that rise once the card is open, and one calm state when it
     cannot be shown. Events on the root, so this file still knows nothing of
     that page, and today's look (shared-card.liquid, ?view=card-classic)
     simply does not listen:
       pp:share-card   the card as the Worker returned it (detail)
       pp:share-open   the card is open and at rest; the controls are armed
       pp:share-gone   it cannot be shown: detail.missing when the link has no code
     And root.ppShare = { shareUrl, copyText } for that page's own copy buttons. */
  function emit(name, detail) {
    try { root.dispatchEvent(new CustomEvent(name, { detail: detail })); } catch (e) { /* an old browser: the page stays as it is */ }
  }

  function say(msg, kind) {
    if (!statusEl) return;
    statusEl.textContent = msg || '';
    statusEl.hidden = !msg;
    statusEl.className = 'pp-share__status' + (kind ? ' is-' + kind : '');
    /* Part 4 fix 10 (10 Oct): a card that cannot be shown has nothing to open or
       close, so the Open / Closed / Back bar goes with it. */
    if (kind === 'bad') {
      var bar = document.querySelector('.pp-share__bar');
      if (bar) bar.hidden = true;
    }
  }

  function token() {
    var p = new URLSearchParams(window.location.search).get('s');
    return p && /^[a-zA-Z0-9]{22}$/.test(p) ? p : null;
  }

  /* The order the link names, forwarded to the Worker so it can check the token
     against that order's own `_Share` property rather than a stored mapping.
     `o` is the API id, `n` the number the customer sees; the confirmation email
     emits both because which one a notification template can print is not
     something to discover after the first order. Absent on links minted before
     2026-09-01, which the Worker still resolves from KV. */
  function orderRef() {
    var q = new URLSearchParams(window.location.search);
    var out = [];
    var o = q.get('o');
    var n = q.get('n');
    if (o && /^[0-9]{1,20}$/.test(o)) out.push('o=' + o);
    if (n && /^[0-9]{1,12}$/.test(n)) out.push('n=' + n);
    return out.length ? '?' + out.join('&') : '';
  }

  function getJson(url) {
    return fetch(url, { credentials: 'omit' }).then(function (r) {
      if (!r.ok) throw new Error(url + ' -> ' + r.status);
      return r.json();
    });
  }

  /* ⭐ THE CARD ITSELF, WITH "BUSY" KEPT APART FROM "NOT AVAILABLE" (2026-10-11). The fulfilment Worker answers
     503 {error:"busy", retry:true} with Retry-After when Shopify is throttling it and nothing is cached. That is
     not a missing card, and saying "This card is not available" to someone holding a real link is wrong twice
     over. So: "One moment…", up to BUSY_TRIES retries honouring Retry-After (capped), and only then a calm
     "Busy right now" with a Try again button. Everything else (404 included) is still the one not-available
     state below. The artists site's copy of this file (artists-worker public/card, synced by hand) reads its
     own routes, which never answer busy, so there it is simply never taken. */
  var BUSY_TRIES = 3;
  function getCard(url, left) {
    if (left == null) left = BUSY_TRIES;
    return fetch(url, { credentials: 'omit' }).then(function (r) {
      if (r.ok) return r.json();
      if (r.status !== 503) throw new Error(url + ' -> ' + r.status);
      var wait = parseFloat(r.headers.get('Retry-After'));
      if (!(wait > 0)) wait = 5;
      wait = Math.min(wait, 15);
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!j || !j.retry) throw new Error(url + ' -> 503');
        if (left <= 0) { var e = new Error('busy'); e.busy = true; throw e; }
        say('One moment…');
        return new Promise(function (res) { setTimeout(res, wait * 1000); }).then(function () { return getCard(url, left - 1); });
      });
    });
  }
  function sayBusy() {
    say('Busy right now, try again in a moment.', 'busy');
    var bar = document.querySelector('.pp-share__bar');
    if (bar) bar.hidden = true;
    if (!statusEl) return;
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'pp-share__retry';
    b.textContent = 'Try again';
    b.addEventListener('click', function () { window.location.reload(); });
    statusEl.appendChild(document.createTextNode(' '));
    statusEl.appendChild(b);
  }

  /* ── painting the card ─────────────────────────────────────────────────
     Everything here is content, not layout. The planes' positions come from
     card-stage.js; this only fills them in. */

  /* ⛔ A COPY OF BORDER_STOPS, and the third one. The original is
     MM.borderStops in projects/pop-cards/print/geometry.mjs, which asserts at
     import that every stop tiles the panel; configurator.js carries the second
     copy for the builder. This page loads neither. Change one, change all
     three — the same standing rule the CARD_FONTS copy below is under.

     Millimetres, against the true 56.25 x 78.75 panel. `cap` is the captioned
     well, `full` the uncaptioned one.

     ⭐ THE 3:4 STRIP ON SCREEN (Yonatan, 22 Oct 2026): the photo planes are 3:4,
     so the panel drawn here is 56.25 x 75 (SHOW_H_MM, as configurator.js). The
     table stays 5:7, because the bake was made from it and print still prints
     it; on screen each stop keeps its paper in mm on every side and its window
     is 3.75 mm shorter, and the 5:7 bake covers that window (paintPhotos). */
  var PANEL_W_MM = 56.25, PANEL_H_MM = 78.75, CAPTION_BAND_MM = 15.1;
  var SHOW_H_MM = PANEL_W_MM * 4 / 3, SHOW_CUT_MM = PANEL_H_MM - SHOW_H_MM;
  var BORDER_STOPS = {
    none:    { cap: { w: 56.25, h: 63.65, top: 0.0 },  full: { w: 56.25, h: 78.75, top: 0.0 } },
    slim:    { cap: { w: 49.25, h: 60.15, top: 3.5 },  full: { w: 49.25, h: 71.75, top: 3.5 } },
    classic: { cap: { w: 46.25, h: 57.65, top: 6.0 },  full: { w: 46.25, h: 68.75, top: 5.0 } },
    wide:    { cap: { w: 40.25, h: 54.65, top: 9.0 },  full: { w: 40.25, h: 62.75, top: 8.0 } },
    super:   { cap: { w: 34.25, h: 51.65, top: 12.0 }, full: { w: 34.25, h: 56.75, top: 11.0 } }
  };

  /* ⛔ THE WELL IS NOT DECORATION — IT IS THE PICTURE'S ASPECT RATIO.
     bakePhoto() crops and scales the uploaded photograph to EXACTLY the well
     the customer chose, and paintPhotos() then stretches that bake across the
     element with width/height 100% (object-fit is deliberately not used: it
     crops inside the element, which the bake cannot reproduce). So if this
     page renders a different well than the card was baked for, every face on
     it is stretched — silently, because nothing is missing and nothing errors.

     ⚠️ THAT IS EXACTLY WHAT SHIPPED. stage.css falls back to the CLASSIC,
     WITH-CAPTIONS well, and this page never wrote the variables at all. On
     order #1021 — border `wide`, layout `full-size` — the bake measured
     0.6417 and the well rendered 0.7940: a 24% horizontal stretch, which is
     Daniel seeing his own photographs squeezed on 2026-09-10.

     The Worker has always returned `border` and `layout`; nothing here read
     them. Radius is NOT written and does not need to be: bakePhoto() fills the
     rounded corners white into the pixels, and the paper behind them is white. */
  function paintWell() {
    var stop = BORDER_STOPS[card.border] || BORDER_STOPS.classic;
    var caps = card.layout !== 'full-size';
    var w = caps ? stop.cap : stop.full;
    stage.style.setProperty('--pp-photo-w', (w.w / PANEL_W_MM * 100).toFixed(3) + '%');
    stage.style.setProperty('--pp-photo-h', ((w.h - SHOW_CUT_MM) / SHOW_H_MM * 100).toFixed(3) + '%');
    stage.style.setProperty('--pp-photo-top', (w.top / SHOW_H_MM * 100).toFixed(3) + '%');
    stage.style.setProperty('--pp-caption-h',
      (caps ? (CAPTION_BAND_MM / SHOW_H_MM * 100) : 0).toFixed(3) + '%');
    /* The rounded corners, drawn by the window since the 3:4 cut: the bake's own
       corners (white in its pixels) are cut off with its top and bottom. The
       fulfilment Worker sends `rounded` and `radius` (the order's _Rounded and
       _Corner Radius) since 24 Oct; without them, square, as the bake now shows. */
    var mm = 0;
    if (card.border !== 'none' && card.rounded === 'on') {
      mm = parseFloat(card.radius);
      if (!(mm > 0 && mm <= 10)) mm = 2;
    }
    stage.style.setProperty('--pp-photo-radius', mm ? (mm / PANEL_W_MM * 100).toFixed(3) + 'cqw' : '0px');
  }

  function paintPhotos() {
    PANELS.forEach(function (name, i) {
      var plane = stage.querySelector('[data-pp-plane="' + name + '"]');
      if (!plane) return;
      var img = plane.querySelector('.pp-plane__img');
      var url = card.photos[i];
      if (img && url) {
        /* The uploaded panel IS the well's contents — bakePhoto() already
           applied the crop and scaled it to the bake size — so it fills the
           element rather than being positioned inside it the way the builder's
           live, still-croppable photo is.
           ⭐ Covering it since the 3:4 strip (Yonatan, 22 Oct: "object-fit: cover
           on the 3:4 panels"): the bake is the 5:7 window, the window on screen is
           3.75 mm shorter, so it loses ~2.5% top and bottom, as in the builder. */
        img.src = url;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.left = '0';
        img.style.top = '0';
        img.style.objectFit = 'cover';
      } else if (img) {
        img.remove();
      }
      var cap = plane.querySelector('.pp-plane__captiontext');
      if (cap) cap.textContent = (card.captions && card.captions[i]) || '';
      /* No caption band at full size — same rule as the builder. */
      var band = plane.querySelector('.pp-plane__caption');
      if (band && card.layout === 'full-size') band.hidden = true;
    });
  }

  /* ⛔ A COPY OF CARD_FONTS, and the copy is deliberate.
     The table lives in assets/configurator.js, which is the builder's bundle
     and is not loaded on this page — there is no shared module between the two.
     The three faces and their Hebrew halves are declared once in
     snippets/font-face.liquid, which layout/theme.liquid renders, so every face
     below is already available here.
     ⚠️ IF A FACE CHANGES IN configurator.js IT MUST CHANGE HERE. A drift shows
     up as the share page rendering a card in a font its owner never chose,
     which is exactly the bug this function exists to fix. */
  var CARD_FONTS = {
    hand:    { family: '"Indie Flower", "Playpen Sans Hebrew", cursive',   weight: 400, scale: 1 },
    classic: { family: '"Fraunces", "Frank Ruhl Libre", Georgia, serif',   weight: 400, scale: 0.80 },
    modern:  { family: '"Figtree", "Heebo", system-ui, sans-serif',        weight: 400, scale: 0.78 },
    // 19 Oct: configurator.js CARD_FONTS, same values.
    typewriter: { family: '"Special Elite", "Heebo", "Courier New", monospace', weight: 400, scale: 0.80 },
    elegant:    { family: '"PP Elegant", "Frank Ruhl Libre", Georgia, serif',   weight: 500, scale: 0.86 }
  };
  var CARD_FONT_DEFAULT = 'hand';
  function cardFont(key) { return CARD_FONTS[key] || CARD_FONTS[CARD_FONT_DEFAULT]; }

  /* THE NOTE AND THE CAPTIONS ARE SET IN THE FACES THE CUSTOMER CHOSE.
     The order carries `_Note Font` and `_Caption Font`, the Worker maps them
     onto the share payload as noteFont/captionFont (SHARE_SCALARS in
     fulfilment/worker.js) — and until 2026-09-10 nothing on this page read
     them. Every shared card rendered in the default hand whatever its owner
     picked, which is what Yonatan saw twice: "looks like the font is different
     than the card I made".

     The six custom properties are the same ones the builder writes from
     cardFontStyle(); stage.css reads them on .pp-cardnote__text and the caption
     band. Set on the share root so both inherit. */
  function paintFonts() {
    var n = cardFont(card.noteFont);
    var c = cardFont(card.captionFont);
    root.style.setProperty('--pp-note-font',   n.family);
    root.style.setProperty('--pp-note-weight', String(n.weight));
    root.style.setProperty('--pp-note-scale',  String(n.scale));
    root.style.setProperty('--pp-cap-font',    c.family);
    root.style.setProperty('--pp-cap-weight',  String(c.weight));
    root.style.setProperty('--pp-cap-scale',   String(c.scale));
  }

  /* The builder's bounds and threshold, and print's — configurator.js
     (NOTE_FIT_MIN / NOTE_FIT_MAX / NOTE_ALIGN_ROWS) and card-art.mjs (a 2.85mm
     floor and a 2.2x ceiling on the same 5.0mm start). Three files, one answer;
     do not let them drift. */
  var NOTE_FIT_MIN = 0.60, NOTE_FIT_MAX = 2.2, NOTE_ALIGN_ROWS = 4;
  /* Blank lines paid for per gap, as the builder and print both do. */
  var NOTE_GAP_MAX = 3;
  /* See the bail in paintNote(): the card is painted while the stage is still
     opening, so the first attempts measure a box with no height. */
  var noteFitTries = 0, NOTE_FIT_TRIES = 12;

  /* ⛔ THE SAME FIT THE CARD AND THE PAPER USE. Rewritten 2026-09-16.
     It used to be "a crude fitter, deliberately": an inline font-size stepped
     down 5% at a time until the text fitted. Three ways that disagreed with
     what the customer approved and with what prints —
       · an inline font-size OVERRIDES stage.css's
         `calc(5.435cqw * var(--pp-note-fit) * ...)`, which is the expression
         that makes a screen millimetre a paper millimetre. The size was then
         a percentage of a base the other two never use;
       · it only ever SHRANK, so a four-word note stayed small on the share
         page while the card and the paper grow it to fill the band;
       · it never wrote --pp-note-align, so a long note stayed centred where
         the card and the paper flip it to `start` past four rows.
     Our first outside tester saw all of it at once: "the text is not
     reflective of what it looked like… the size is different, the alignment is
     different", and wondered whether the print would be wrong too. Daniel
     checked the paper: the print is right and this page was the liar
     (2026-09-16).
     So this is the builder's closed-note path, ported: words decide the size,
     the blank lines go back into whatever room is left, and the alignment is
     counted on the final content at the final size. */
  function paintNote() {
    var box = root.querySelector('[data-pp-note-box]');
    var text = root.querySelector('[data-pp-note-text]');
    if (!box || !text) return;

    /* Leading and trailing blank lines are dropped — the note is centred in its
       band, so they cannot be seen and can only shove the words off centre and
       shrink them. Blank lines BETWEEN words are kept: that is the gap between
       a message and a signature. Same rule as trimBlankEdges() in card-art.mjs. */
    var lines = String(card.note == null ? '' : card.note).split('\n');
    while (lines.length && !lines[0].trim()) lines.shift();
    while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
    var hasWords = lines.some(function (l) { return l.trim(); });
    if (!hasWords) { box.hidden = true; return; }
    box.hidden = false;

    /* The old inline size, if this page was rendered before the rewrite in the
       same session. The size belongs to --pp-note-fit now. */
    text.style.removeProperty('font-size');

    var cs = getComputedStyle(box);
    var availH = box.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    var availW = box.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    /* ⛔ NOT LAID OUT YET — AND ON THIS PAGE THAT IS THE NORMAL FIRST CALL.
       Measured on Draft, 2026-09-16: the note is painted while the stage is
       still opening, so the box is 0 tall, every size "fits", and an early
       return leaves the note at the stylesheet's own --pp-note-fit of 1. That
       is worse than the crude fitter this replaced: the long note then
       OVERFLOWED the band (163px of text in a 146px box) instead of being
       wrongly shrunk. Caught because the share page was compared against the
       builder rather than eyeballed.

       The builder's fitNote() has a bounded retry for exactly this state
       (_noteFitRetries). Bounded, for its reason too: a hidden stage is a
       state, not a race — reveal() re-fits the moment the card is up, and
       fonts.ready re-fits after that. */
    if (!(availH > 0) || !(availW > 0)) {
      text.textContent = lines.join('\n');
      if (noteFitTries < NOTE_FIT_TRIES) { noteFitTries++; setTimeout(paintNote, 120); }
      return;
    }
    noteFitTries = 0;

    /* ⭐ THE SIZE PAYS FOR THE GAPS (2026-09-16). A blank line the customer
       typed is part of the card, so the handwriting shrinks to make room for it
       — which is what `fitNote()` does and, since the same day, what
       `print/card-art.mjs` does. This file briefly sized on the words alone,
       copying print's older rule; that made a gapped note render bigger here
       than on the card the customer approved. Daniel's rule settles the
       direction: what the customer sees prints, and what prints is what they
       saw, so all three pay for gaps up to NOTE_GAP_MAX. */
    var sizing = [];
    var gapRun = 0;
    for (var s = 0; s < lines.length; s++) {
      if (lines[s].trim()) { gapRun = 0; sizing.push(lines[s]); }
      else if (++gapRun <= NOTE_GAP_MAX) { sizing.push(lines[s]); }
    }
    text.textContent = sizing.join('\n');

    var fits = function (m) {
      box.style.setProperty('--pp-note-fit', String(m));
      /* +0.5 absorbs scrollHeight's integer rounding, exactly as the other two
         do — without it a size that fits exactly is refused. */
      return text.scrollHeight <= availH + 0.5 && text.scrollWidth <= availW + 0.5;
    };

    var lo = NOTE_FIT_MIN, hi = NOTE_FIT_MAX, best = null;
    if (fits(NOTE_FIT_MAX)) {
      best = NOTE_FIT_MAX;
    } else {
      for (var i = 0; i < 9; i++) {
        var mid = (lo + hi) / 2;
        if (fits(mid)) { best = mid; lo = mid; } else { hi = mid; }
      }
      /* ⛔ THE FLOOR IS A CANDIDATE, NOT JUST A FALLBACK — the bisection only
         ever tests points strictly above it. The identical line is in
         configurator.js and card-art.mjs; it is why a note that fits only at
         the floor is not reported as not fitting. */
      if (best == null && fits(NOTE_FIT_MIN)) best = NOTE_FIT_MIN;
    }
    box.style.setProperty('--pp-note-fit', String(best == null ? NOTE_FIT_MIN : best.toFixed(3)));

    /* The blank lines go back, down to the bottom of the card: whatever the
       words left of the band, in whole lines, is the budget. Earlier gaps are
       kept first, so it is the run at the bottom that stops.

       ⛔ MEASURED AGAINST THE WORDS ALONE, which is NOT what the size was fitted
       to. Read off the element while it still holds the gaps, this would count
       them as words, find no room, and drop the very gap the size just paid
       for. Two measurements, two questions — the builder and card-art.mjs both
       re-measure here for exactly this reason. */
    text.textContent = lines.filter(function (l) { return l.trim(); }).join('\n');
    var lh = parseFloat(getComputedStyle(text).lineHeight) || 1;
    var maxLines = Math.max(1, Math.floor((availH + 0.5) / lh));
    var budget = Math.max(0, maxLines - Math.round(text.scrollHeight / lh));
    var shown = [];
    for (var b = 0; b < lines.length; b++) {
      if (lines[b].trim()) shown.push(lines[b]);
      else if (budget > 0) { shown.push(lines[b]); budget--; }
    }
    text.textContent = shown.join('\n');

    /* Centred when short, `start` when long — counted on the FINAL content at
       the FINAL size, because the same words are five rows at one size and nine
       at another and it is the rendered shape that decides. `start`, never
       `left`: the <p> carries dir="auto", and a Hebrew note flushed left is
       ragged on the wrong edge. */
    var arows = Math.round(text.scrollHeight / (parseFloat(getComputedStyle(text).lineHeight) || 1));
    box.style.setProperty('--pp-note-align', arows > NOTE_ALIGN_ROWS ? 'start' : 'center');

    /* Past the floor the words are longer than the card. Clamp at a whole line
       so it reads as unfinished rather than as a rendering fault — the band is
       still the hard cap through stage.css's max-height. */
    if (best == null) text.style.maxHeight = 'min(100%, ' + (maxLines * lh).toFixed(2) + 'px)';
    else text.style.removeProperty('max-height');
  }

  /* ⛔ THE NOTE IS RE-FITTED WHEN ITS BOX CHANGES SIZE, NOT ON A TIMER.
     (2026-09-20. Yonatan, on a real phone: the note "cut off in the after
     sales preview".)

     The bail in paintNote() retries on a 120ms timer, twelve times — 1.44
     seconds, and then it gives up for good. On this machine the stage opens
     long inside that; on a phone it does not have to. When the timer runs out
     first, `--pp-note-fit` is left at the 1 that stage.css declares as its
     no-JS answer — about 1.5x the size this note needed — and `max-height:
     100%` then cuts the overflow off mid-row. That is the screenshot: a note
     rendered too big and sliced, not a note that was sized wrongly.

     ⚠️ THE SAME MISTAKE THE FOOT ALREADY LEARNED. watchFoot() says it outright
     — "a ResizeObserver rather than a one-off after fonts.ready" — because the
     first answer is given before the layout has settled. The note kept the
     one-off. It now watches its own box, so every later settle re-fits: a slow
     open, a font landing, an orientation change, the desktop zoom promoting
     the stage to a full-viewport surface.

     No feedback loop: this box is positioned by percentages off the note
     PLANE, so its own size cannot depend on the type inside it — changing
     --pp-note-fit moves the text and never the box. The height guard makes
     that explicit rather than trusting it. */
  var noteRO = null, lastNoteBoxH = -1;
  function watchNoteBox() {
    if (noteRO || typeof ResizeObserver !== 'function') return;
    var box = root.querySelector('[data-pp-note-box]');
    if (!box) return;
    noteRO = new ResizeObserver(function () {
      var h = box.clientHeight;
      if (h === lastNoteBoxH || !(h > 0)) return;
      lastNoteBoxH = h;
      try { paintNote(); } catch (e) { /* the note is already on the page */ }
    });
    noteRO.observe(box);
  }

  function paintCover(designs) {
    /* BY THE ART KEY FIRST (2026-10-06). An order since 2026-09-10 carries
       `_Design Id`, the catalogue id, and the share route passes it on as
       `designId`. The NAME was the key here until then, which the property
       contract had already retired: a design can be renamed, and two artists
       can call a card the same thing. When the order names an id, only the id
       counts, so a withdrawn design shows no art rather than a namesake's.
       Orders without one (before 2026-09-10, and the store's own designs until
       2026-09-17) are matched by name, permanently. Either way, fall back to
       no artwork rather than to the wrong card — a stranger's first sight of
       the product should not be somebody else's cover.

       Returns whether the design was found, so the boot knows whether to wait
       for the artist catalogue. */
    var wantId = String(card.designId || '').toLowerCase();
    var want = String(card.design || '').toLowerCase();
    var list = (designs && designs.designs) || [];
    var d = null;
    for (var i = 0; i < list.length; i++) {
      var hit = wantId
        ? String(list[i].artKey || list[i].id).toLowerCase() === wantId
        : (String(list[i].id).toLowerCase() === want || String(list[i].name).toLowerCase() === want);
      if (hit) { d = list[i]; break; }
    }
    if (!d) return false;
    if (!d.art) return true;
    ['CoverFront', 'CoverTop'].forEach(function (face) {
      var img = stage.querySelector('[data-pp-plane="' + face + '"] .pp-plane__art');
      if (!img || !d.art[face]) return;
      // Artist designs carry absolute URLs; the store's own carry bare filenames.
      img.src = /^(https?:)?\/\//.test(d.art[face]) ? d.art[face] : (cfg.assetBase || '') + d.art[face];
      img.hidden = false;
    });
    paintStrip(d);
    paintTop(d);
    paintGround(d);
    /* THE BACK, for the page's Back view (2026-09-24; on the 3D card since v2e,
       18 Oct): the design's back art on CoverBack, the outside of the bottom
       page, which faces the camera only when the card turns over (card-back).
       No paper layer, as in the builder: the art is the paper. Published on
       the stage too, which is how the section knows to show its Back button;
       a design with no web back keeps it hidden. */
    if (d.art.Back) {
      var backUrl = /^(https?:)?\/\//.test(d.art.Back) ? d.art.Back : (cfg.assetBase || '') + d.art.Back;
      var backImg = stage.querySelector('[data-pp-plane="CoverBack"] .pp-plane__art');
      if (backImg && backImg.getAttribute('src') !== backUrl) { backImg.src = backUrl; backImg.hidden = false; }
      stage.setAttribute('data-pp-back', backUrl);
    }
    return true;
  }

  /* A MOCKUP'S INSIDE BOTTOM (2026-10-12). A mockup (card.mockup, from pp-fulfilment's /s/ route: a custom
     card made from its two print files on artists.picturepops.com/admin/mockups) has its note printed in the
     art, so the inside bottom is drawn as the design's ground, under an empty note, as the builder draws it
     (snippets/builder-stage.liquid). Only for a mockup: an order's card keeps today's look. The image goes
     before the plane's paper layer, which card-paper.js then dresses from it. */
  function paintGround(d) {
    if (!card.mockup || !d.art.CoverBottomGround) return;
    var plane = stage.querySelector('[data-pp-plane="CoverBottom"]');
    if (!plane || plane.querySelector('.pp-plane__art--ground')) return;
    var img = document.createElement('img');
    img.className = 'pp-plane__art pp-plane__art--ground';
    img.alt = ''; img.draggable = false; img.crossOrigin = 'anonymous';
    img.src = /^(https?:)?\/\//.test(d.art.CoverBottomGround) ? d.art.CoverBottomGround : (cfg.assetBase || '') + d.art.CoverBottomGround;
    plane.insertBefore(img, plane.firstChild);
  }

  /* THE CUSTOMER'S INSIDE TOP (2026-10-08). On a generic-cover design the customer chose their own
     words or a photo for the inside top; the order carries it as one layer (`_Photo 5`), which the
     share route hands over as card.top. It is drawn here exactly as the builder previewed it and as
     print draws it: the design's text-free top (CoverTopPlain), the layer over it, the glue band
     bare. Without this the page showed the design's own inside top, which is the very greeting that
     was stripped: a sympathy line on a birthday card.

     The layer is the print master, 98 x 123 mm with its 3 mm bleed; the plane shows the 92 x 120 trim,
     so it is cropped by the bleed on the way in. Both images are CORS-clean (Cloudinary and the
     artists catalogue answer ACAO *), so the canvas can be read back. Any failure leaves the design's
     own top on the plane, never a blank one. Idempotent: paintCover runs twice when the design is an
     artist's (own catalogue, then the artist catalogue). */
  var topPainted = false;
  function paintTop(d) {
    var t = card.top;
    if (topPainted || !t || !t.layer || !d.art) return;
    var img = stage.querySelector('[data-pp-plane="CoverTop"] .pp-plane__art');
    if (!img) return;
    topPainted = true;
    var abs = function (u) { return /^(https?:)?\/\//.test(u) ? u : (cfg.assetBase || '') + u; };
    var load = function (u) {
      return new Promise(function (res) {
        if (!u) { res(null); return; }
        var i = new Image();
        i.crossOrigin = 'anonymous';
        i.onload = function () { res(i); };
        i.onerror = function () { res(null); };
        i.src = u;
      });
    };
    var base = d.art.CoverTopPlain || d.art.CoverTop;
    Promise.all([load(base ? abs(base) : ''), load(t.layer)]).then(function (r) {
      var b = r[0], layer = r[1];
      if (!layer) { topPainted = false; return; }
      var W = 1000, H = 1332;                         // the web face, as the builder's preview
      var c = document.createElement('canvas'); c.width = W; c.height = H;
      var g = c.getContext('2d');
      if (b) g.drawImage(b, 0, 0, W, H); else { g.fillStyle = '#fff'; g.fillRect(0, 0, W, H); }
      var L = document.createElement('canvas'); L.width = W; L.height = H;
      var lg = L.getContext('2d');
      var sx = layer.naturalWidth / 98, sy = layer.naturalHeight / 123;
      lg.drawImage(layer, 3 * sx, 3 * sy, 92 * sx, 120 * sy, 0, 0, W, H);
      lg.clearRect(10 / 92 * W, 80 / 120 * H, 72 / 92 * W, H);   // the glue band, trim x 10-82 from y 80
      g.drawImage(L, 0, 0);
      try {
        c.toBlob(function (blob) {
          if (!blob) return;
          img.src = URL.createObjectURL(blob);
          img.hidden = false;
        }, 'image/jpeg', 0.9);
      } catch (e) { /* a tainted canvas: the design's own top stays */ }
    });
  }

  /* THE PHOTO STRIP BORDER (Part B, 2026-09-19), when the design has one: each
     photo panel shows its own quarter of the 218 x 78.9 mm strip as its
     BACKGROUND, so the photo covers it and the rounded photo corners show it —
     what prints there. Same arithmetic as panelStrip() in configurator.js;
     none at the `none` stop, where the photo covers everything and print does
     not print the border. */
  function paintStrip(d) {
    if (!d.art.Strip || card.border === 'none') return;
    var url = /^(https?:)?\/\//.test(d.art.Strip) ? d.art.Strip : (cfg.assetBase || '') + d.art.Strip;
    stage.querySelectorAll('[data-pp-panel]').forEach(function (el) {
      var i = parseInt(el.getAttribute('data-pp-panel'), 10);
      if (!(i >= 0 && i < 4)) return;
      el.style.backgroundImage = 'url("' + url + '")';
      // 3:4 panel: the art's 5:7 height cut top and bottom, centred (53.5 wide is 71.333 tall).
      el.style.backgroundSize = (218 / 53.5 * 100).toFixed(3) + '% ' + (78.9 / (53.5 * 4 / 3) * 100).toFixed(3) + '%';
      el.style.backgroundPosition = ((2 + 53.5 * i) / 164.5 * 100).toFixed(3) + '% 50%';
      el.style.backgroundRepeat = 'no-repeat';
    });
  }

  /* ── the camera ────────────────────────────────────────────────────────
     Three custom properties on the stage, exactly as the builder writes them.
     The union of the drawn planes' boxes is in unzoomed px about the scene
     centre, so the fit is a scale and a translate and nothing else. */

  function union(boxes) {
    var x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    boxes.forEach(function (b) {
      if (!b) return;
      x0 = Math.min(x0, b.cx - b.w / 2); x1 = Math.max(x1, b.cx + b.w / 2);
      y0 = Math.min(y0, b.cy - b.h / 2); y1 = Math.max(y1, b.cy + b.h / 2);
    });
    if (!isFinite(x0)) return null;
    return { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, w: x1 - x0, h: y1 - y0 };
  }

  /* ⛔ THE SHADOW POOL IS PART OF THE CARD, AS FAR AS FRAMING GOES.
     .pp-ground is placed in comp units — 0.844 wide, 0.1101 tall, centred
     0.4884 below the scene centre — which puts its bottom edge BELOW the
     lowest plane. Framing on the planes alone crops it: seen in Chrome at
     1512x828, the pool ran to y=648 in a 600px stage and was cut off dead
     straight at the edge, which is what "something doesn't look right with
     the shadows" was.

     Adding it to the union fixes both axes and both layouts at once — the
     scale accounts for it and so does the centring — where the earlier
     bottom-align only helped when the fit was limited by width. */
  function groundBox() {
    var comp = (geom && geom.comp && geom.comp.width)
      ? geom.comp.width / window.PP.CardStage.LAYOUT_SCALE : 0;
    if (!comp) return null;
    return { cx: 0, cy: comp * 0.4884, w: comp * 0.844, h: comp * 0.1101 };
  }

  /* Just the four photo panels — the concertina, without the cover above it or
     the shadow pool below. ⭐ THIS, NOT THE UNION, IS WHAT DANIEL MEANS BY THE
     CARD'S MIDDLE: "I'd like the card to be centered so that the midway point
     of the panels is in line with the buttons." The union's centre sits ~70px
     higher, because the tall cover panel and the pool pull it around; on a
     1440x900 desktop that was exactly the offset measured. */
  function panelBoxFor(frame) {
    var t = window.PP.CardStage.frameTransforms(geom, frame);
    var out = [];
    PANELS.forEach(function (k) {
      if (t.planes[k] && t.planes[k].facing) out.push(t.planes[k].box);
    });
    return union(out);
  }

  function boxesFor(frame) {
    var t = window.PP.CardStage.frameTransforms(geom, frame);
    var out = [];
    Object.keys(t.planes).forEach(function (k) {
      if (t.planes[k].facing) out.push(t.planes[k].box);
    });
    return out;
  }

  /* Where the camera would settle for a union — worked out without moving the
     card, so the fold can know both ends before it starts. `anchor` is the box
     that gets lined up with the buttons on desktop; for the open card that is
     the panel band, for the shut one it is the cover, because a closed card
     has no panels to be level with. */
  function fitFor(u, anchor) {
    if (!u || !stage.clientWidth || !stage.clientHeight) return null;
    var sw = stage.clientWidth, sh = stage.clientHeight;
    var scale = Math.min(sw / u.w, sh / u.h) * PAD;

    /* ⛔ THE CARD SITS LOW ON A PHONE, AND CENTRED ON DESKTOP — because the
       two layouts stack differently and the leftover height means opposite
       things in each.

       PHONE, one column: the card is a wide concertina in a stage as tall as
       the screen allows, so the fit is limited by WIDTH and there is always
       height to spare. Centring splits that spare above and below, and the
       half below lands between the card and the tagline — the gap Daniel drew
       on. Bottom-aligning puts all of it above the card, where it reads as air
       under the nav rather than a hole above the button.

       ⭐ DESKTOP, two columns: the copy and the buttons are vertically centred
       in the LEFT column, and the card has the right one to itself. Pushing it
       to the bottom of its column drops it below the buttons it is supposed to
       sit beside. Daniel, 2026-09-10: "the card isn't centered on the screen.
       I'd like the card to be centered so that the midway point of the panels
       is in line with the buttons." Centring the union does exactly that —
       `centred` puts u.cy on the stage's middle, and the stage is full height,
       so the card's middle IS the screen's middle, which is where the buttons
       are.

       ⚠️ Matches the 900px breakpoint in shared-card.liquid, which is where
       the layout actually becomes two columns. Read live rather than cached:
       a window can be dragged across it. */
    var BOTTOM_M = 16;

    /* The union already includes the pool (see groundBox), so the lowest
       thing painted IS the bottom of the union. */
    var centred = -u.cy * scale;
    var low = sh / 2 - BOTTOM_M - (u.cy + u.h / 2) * scale;
    /* ⚠️ NOT WHILE THE STAGE IS AN OVERLAY. Zoomed on desktop the stage is a
       fixed, full-viewport surface and the buttons are underneath it — lining
       the card up with something it is covering would be nonsense. Centre it
       instead, which is what a lightbox should do anyway. */
    var twoColumn = window.matchMedia &&
                    window.matchMedia('(min-width: 900px)').matches &&
                    !stage.classList.contains('is-zoomed');

    var camY;
    if (!twoColumn) {
      camY = Math.max(centred, low);
    } else {
      /* ⭐ DESKTOP: LEVEL WITH THE BUTTONS, AND SIZED FOR THE ROOM THAT LEAVES.
         Daniel, 2026-09-10: "I'd like the card to be centered so that the
         midway point of the panels is in line with the buttons."

         ⛔ THIS CANNOT BE DONE BY MOVING THE CAMERA ALONE, and two attempts
         that tried it both failed silently. The fit above sizes the card to
         FILL the stage — that is what `scale` means — so the paper's bottom is
         already on the stage's bottom edge, to the pixel: measured at 737px in
         a stage running 50→737. There is no room to slide it anywhere, and a
         clamp that keeps the card on screen will therefore always win, which
         is exactly what it did (twice, once against the shadow pool and once
         against the paper).

         So the SIZE has to give. The band available to a card centred on the
         buttons is symmetric about them — anything past the nearer edge is
         unreachable — and the card is fitted into that band instead of into
         the whole stage. It comes out slightly smaller and exactly where it
         was asked to be, which is the right trade: nobody can see 6% of scale,
         everybody can see a card that does not line up.

         The two bounds below are that band, solved rather than iterated. With
         camY chosen so the panels land on the buttons, the union's top and
         bottom are both linear in `scale`, so the largest scale that keeps
         both on screen is a min of two ratios. */
      var acts = root.querySelector('.pp-share__acts');
      var aim = anchor || panelBox;
      if (acts && aim) {
        var sr = stage.getBoundingClientRect();
        var ar = acts.getBoundingClientRect();
        var btn = ar.top + ar.height / 2 - sr.top;   /* buttons, stage-relative */

        /* Where the union sits relative to the panels' middle. */
        var rel = u.cy - aim.cy;
        var topCoef = rel - u.h / 2;                 /* union top  = btn + topCoef * scale */
        var botCoef = rel + u.h / 2;                 /* union bottom = btn + botCoef * scale */

        if (topCoef < 0) scale = Math.min(scale, btn / -topCoef);
        if (botCoef > 0) scale = Math.min(scale, (sh - btn) / botCoef);
        /* A pathological layout must not shrink the card to nothing. */
        var floorScale = Math.min(sw / u.w, sh / u.h) * PAD * 0.5;
        if (!(scale > 0) || scale < floorScale) scale = floorScale;

        camY = btn - sh / 2 - aim.cy * scale;
      } else {
        camY = centred;
      }
    }
    return { x: -u.cx * scale, y: camY, scale: scale };
  }

  /* Write a fit straight to the stage. No transition juggling: every caller
     either has .pp-viewport--opening on (which kills the easing) or wants the
     glide. */
  function applyFit(f) {
    if (!f) return;
    camX = f.x; camY = f.y; camScale = f.scale;
    stage.style.setProperty('--pp-cam-x', camX.toFixed(2) + 'px');
    stage.style.setProperty('--pp-cam-y', camY.toFixed(2) + 'px');
    stage.style.setProperty('--pp-cam-scale', camScale.toFixed(4));
    if (spin.on && spin.shaded) placeComp(spin.vid, f);
  }

  function writeCam(u) { applyFit(fitFor(u)); }

  /* ── NO STANDING UP SINCE v2e ─────────────────────────────────────────────
     This was a port of configurator.js _standGeom: the artist's old camera
     looked at the shut card from below (pitch -69.4 against the cover's
     -91.8), so the closed cover was a keystone and the camera was re-pitched
     by hand to stand it square. Yonatan's v2e frame 100 looks straight down
     on the cover (camera -89.9, cover -90), so the shut card is square in the
     geometry itself and every clip starts and ends there. Removed 18 Oct. */

  /* The union of a geometry's facing planes at a frame — the shape the camera
     has to fit. No ground pool: it is placed for the OPEN card and a standing
     closed one has nothing to cast it. */
  function unionAt(g, frame) {
    var t = window.PP.CardStage.frameTransforms(g, frame);
    var out = [];
    Object.keys(t.planes).forEach(function (k) {
      if (t.planes[k].facing) out.push(t.planes[k].box);
    });
    return union(out);
  }

  function calibrate() {
    window.PP.CardStage.calibrate(geom, stage.clientWidth || window.innerWidth,
      stage.clientHeight || window.innerHeight, window.devicePixelRatio || 1);
  }

  /* ── the poses ─────────────────────────────────────────────────────────
     TWO AXES since v2e (Yonatan's render, 17 Oct). `main` is card-geometry.json:
     frame 100 shut, front up, 0 open. `back` is card-geometry-back.json: frame 0
     shut, front up (the same pose as main 100), to its last frame (59), the
     card turned over with CoverBack to the camera. Every pose the card is ever
     in is { axis, g }, and every move is a clip run along one axis.

     The rest poses, by the page's three views:
       open    main 0
       closed  main 100   (= back 0)
       back    back 59 */
  var pos = { axis: 'main', g: FRAME_CLOSED };
  var backGeom = null;

  function geoOf(axis) { return axis === 'back' ? backGeom : axis === 'spin' ? spin.geom : geom; }
  function lastOf(axis) {
    var g = geoOf(axis);
    return (g && g.frames && g.frames.last != null) ? g.frames.last : (axis === 'back' ? 59 : axis === 'spin' ? 119 : FRAME_CLOSED);
  }
  function restOf(p) {
    if (p.axis === 'main' && p.g === FRAME_OPEN) return 'open';
    if ((p.axis === 'main' && p.g === FRAME_CLOSED) || (p.axis === 'back' && p.g === 0)) return 'closed';
    if (p.axis === 'back' && p.g === lastOf('back')) return 'back';
    return '';
  }
  function restPos(view) {
    if (view === 'open') return { axis: 'main', g: FRAME_OPEN };
    if (view === 'back' && backGeom) return { axis: 'back', g: lastOf('back') };
    return { axis: 'main', g: FRAME_CLOSED };
  }

  /* ⚠️ .pp-viewport--closed IS TIED TO THE POSE (stage.css says why): the four
     leaves, their paper backs and the note are hidden while the card is shut
     and coincident — main frames 98 and up, and the whole of the turn over,
     which is a shut card from end to end. */
  function posePos(axis, g) {
    var G = geoOf(axis);
    if (!G) return;
    pos = { axis: axis, g: g };
    window.PP.CardStage.apply(stage, G, g);
    stage.classList.toggle('pp-viewport--closed', axis === 'back' || (axis === 'main' && g >= 98));
  }

  function apply(frame) { posePos('main', frame); }

  /* The shut card's box (front up and back up are the same sheet, the same
     size), and its framing. A shut card has no panel band to be level with,
     so the cover is its own anchor. */
  var shutBox = null;
  function viewFit(view) {
    if (view === 'open') return fitFor(openBox, panelBox);
    if (view === 'spin') return spinBox ? fitFor(spinBox, spinBox) : fitFor(openBox, panelBox);
    return fitFor(shutBox, shutBox) || fitFor(openBox, panelBox);
  }

  function reframe() {
    /* Already on from the markup on first load; this is for the resize path,
       which re-frames and must not glide to the new camera. */
    stage.classList.add(OPENING_CLASS);
    calibrate();
    openBox = union(boxesFor(FRAME_OPEN).concat([groundBox()]));
    /* The PAPER only — every facing plane, without the shadow pool. openBox
       has to include the pool so the FIT does not crop it (see groundBox), but
       the pool must not get a vote on how far the card may be nudged: it is
       soft shadow, the stage is overflow:visible at rest, and letting it run a
       little past the bottom edge costs nothing. */
    cardBox = union(boxesFor(FRAME_OPEN));
    panelBox = panelBoxFor(FRAME_OPEN);
    shutBox = unionAt(geom, FRAME_CLOSED);
    spinBox = spinUnion();
    lastW = stage.clientWidth; lastH = stage.clientHeight;
    writeCam(openBox);
  }

  /* ⛔ THE ALIGNMENT HAS TO BE RE-RUN WHEN THE COLUMN MOVES.
     The desktop camera puts the panels level with the button row, so it
     depends on where that row IS — and on first paint it is not where it will
     end up. "Look what I made" is set in Caveat, a webfont; until it arrives
     the headline is measured in a fallback of different metrics, the block is
     a different height, and the buttons sit somewhere else.

     ⚠️ MEASURED, NOT SUPPOSED. The camera stored camY = -8.94 while the
     settled layout wanted 36.41 — a 45px error, and the second half of the
     70px Daniel reported. Nothing was clamped and the arithmetic was right;
     it was simply answered too early. This is the same class of mistake as
     fitting text before fonts.ready, which this file already guards against
     for the note.

     A ResizeObserver rather than a one-off after fonts.ready: the foot also
     changes height when a label wraps, when the window is resized across the
     breakpoint, or when a merchant retitles a button in the theme editor. */
  function realign() {
    /* Not mid-zoom — the fit would be written underneath a live zoom. The
       deferred path already exists for that; see refitPending. */
    if (!geom || !openBox || zoomOn) { refitPending = zoomOn; return; }
    /* ⚠️ AND NOT MID-MOVE. A move drives the camera frame by frame between
       two fits it worked out when it started; a re-fit landing in the middle
       of that is a jump the animation then eases away from. */
    if (moving) return;
    restCamera();
  }

  /* The camera for whatever state the card is at rest in. */
  function restCamera() { applyFit(viewFit(restView)); }

  function watchFoot() {
    /* ⛔ WATCH THE STAGE AS WELL AS THE FOOT, or most of the moves are missed.
       They are the two children of a column flex: the stage is `flex: 1 1 auto`
       and the foot `flex: 0 0 auto`, so when the STAGE's height settles the
       foot slides up or down without its own box changing by a pixel — and a
       ResizeObserver on the foot alone never hears about it. */
    var foot = root.querySelector('.pp-share__foot');
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { try { realign(); } catch (e) {} });
    }
    if (typeof ResizeObserver === 'undefined') return;
    var ro = new ResizeObserver(function () { realign(); });
    if (foot) ro.observe(foot);
    ro.observe(stage);
  }

  /* ⛔ .pp-viewport--opening IS NOT DECORATION. stage.css hangs three things
     off it and the builder sets it for exactly the length of the opening:

       .pp-zoom { transition: none }  the camera must SNAP to the framing the
         move writes on every frame; .pp-zoom's 520ms transform transition
         would ease behind it and the scene would drift under the animation.
       .pp-zoom { filter: none }      the card's drop-shadow re-rasters
         .pp-zoom, and during a move that is once per frame, on a phone.
       .pp-ground { display: none }   the pool is sized and placed for the
         OPEN card. Under a folded, rising one it sits detached below it.
       .pp-shade { display: none }    the clip's own shading is on the canvas.

     Here it is on for every move, the opening and the page's Open / Closed /
     Back alike, and comes off at rest, which is what reads as the card coming
     to rest. */
  var OPENING_CLASS = 'pp-viewport--opening';

  /* ── THE CLIPS (v2e, 18 Oct: Daniel, "Move share page to V2e") ────────────
     Yonatan's renders, each a shading pass on white with the camera of its own
     geometry, played under the CSS planes frame for frame:

       open   main, video frame i = geometry 100 - i
       close  main, video frame i = geometry i
       back   back, video frame i = geometry i         (shut, front up → back up)
       rev    back, video frame i = geometry 59 - i    (back up → front up)

     The same four the design page links its states with (rd-card3d.js
     RDF3D.views) and the builder opens with (configurator.js startOpening).

     ⭐ THE 1000 px COPIES ON A PHONE. A card painted at most 1200 device px
     wide gains nothing from the 2000 px clip, and the small one is under half
     the bytes. Chosen once, from the viewport, as the builder's
     prefetchOpenClip() does: the card can never paint more than the shorter
     edge, so this errs toward the big file and never toward a soft one.

     ⭐ FETCHED WHOLE, PLAYED FROM MEMORY. Safari does not honour
     preload="auto", and a clip that streams stalls on its first frames, which
     are the ones the eye is on (card-moves.js and Yonatan's own player do the
     same). The opening's fetch starts at boot, the rest once the card is open.
     A fetch that fails falls back to the address itself, streamed. */
  var SMALL = (function () {
    var b = Math.min(window.innerWidth || 0, window.innerHeight || 0) * (window.devicePixelRatio || 1);
    return !!b && b <= 1200;
  })();
  var CLIPS = cfg.clips || {};
  if (!CLIPS.open && vid && vid.getAttribute('src')) CLIPS.open = { big: vid.getAttribute('src') };
  var SHADES = cfg.shades || {};
  var CLIP_AXIS = { open: 'main', close: 'main', back: 'back', rev: 'back' };
  var CLIP_REVERSED = { open: true, rev: true };
  var BLOBS = {}, VIDS = {};

  function pick(o) { return o ? ((SMALL && o.small) || o.big || '') : ''; }
  function clipBlob(name) {
    var url = pick(CLIPS[name]);
    if (!url) return Promise.resolve('');
    if (!BLOBS[url]) {
      BLOBS[url] = (window.fetch ? fetch(url).then(function (r) {
        if (!r.ok) throw new Error('clip ' + r.status);
        return r.blob();
      }).then(function (b) { return URL.createObjectURL(b); }) : Promise.reject(new Error('no fetch')))
        .catch(function () { return url; });
    }
    return BLOBS[url];
  }
  /* One decoder per clip, made the first time it is asked for. The opening's
     is the one in the markup. Never displayed: the canvas paints. */
  function clipVid(name) {
    if (VIDS[name]) return VIDS[name];
    var v = (name === 'open' && vid) ? vid : null;
    if (!v) {
      v = document.createElement('video');
      v.className = 'pp-shadesrc';
      ['muted', 'playsinline', 'disablepictureinpicture', 'disableremoteplayback'].forEach(function (a) { v.setAttribute(a, ''); });
      v.setAttribute('aria-hidden', 'true');
      v.preload = 'auto';
      stage.appendChild(v);
    }
    v.muted = true;
    v.playsInline = true;
    VIDS[name] = v;
    if (!v.getAttribute('src')) {
      clipBlob(name).then(function (src) { if (src && !v.getAttribute('src')) { v.src = src; try { v.load(); } catch (e) {} } });
    }
    return v;
  }

  /* Each rest pose's shading still, black + alpha inside the camera (stage.css
     .pp-shade): the open card's, the shut card's (card-open's frame 0) and the
     turned card's (card-back's last). Swapped only when the pose changes. */
  function setShade(view) {
    var el = root.querySelector('[data-pp-shade]');
    var url = pick(SHADES[view]);
    if (!el || !url || el.getAttribute('data-pp-shade-for') === view) return;
    el.setAttribute('data-pp-shade-for', view);
    el.style.backgroundImage = 'url("' + url + '")';
  }

  function loadBackGeom() {
    if (backGeom) return Promise.resolve(backGeom);
    if (!cfg.backGeom) return Promise.reject(new Error('no back geometry'));
    if (!loadBackGeom.p) {
      loadBackGeom.p = getJson(cfg.backGeom).then(function (g) {
        if (!g || !g.camera || !g.planes) throw new Error('bad back geometry');
        backGeom = g;
        return g;
      });
      loadBackGeom.p.catch(function () { loadBackGeom.p = null; });
    }
    return loadBackGeom.p;
  }

  /* ── THE SHADING CANVAS ──────────────────────────────────────────────────
     The clip is drawn here, multiplied over the card from OUTSIDE .pp-zoom
     (video has no alpha), inside the same callback that poses the planes —
     one commit, so the pixels, the pose and the timestamp are the same
     instant. Since v2e the camera moves during a clip (the shut card is
     framed bigger than the open one), so the canvas is placed on every frame
     from the camera this file is writing, never from CSS.

     ⛔ THE BAND IS 200/2000 AND SMOOTHSTEP. H.264 rounds the authored white
     border to 254, a uniform 0.4% darkening over the whole comp square;
     across a large flat ground with a hard boundary that is a visible grey
     rectangle. The fade erases the border to nothing (destination-out), and
     a straight ramp left Mach bands where it started and stopped. */
  var shadeCtx = null, shadeEdges = null;
  function shadeSize(scale) {
    if (!cvs) return;
    var css = (COMP_SPAN / window.PP.CardStage.LAYOUT_SCALE) * scale;
    var px = Math.max(16, Math.min(SHADE_CANVAS_MAX, Math.round(css * (window.devicePixelRatio || 1))));
    shadeCtx = cvs.getContext('2d', { alpha: true });
    if (cvs.width !== px) {
      cvs.width = px; cvs.height = px;
      shadeEdges = null;
    }
    /* AFTER the resize, never before: assigning canvas.width resets the 2D
       context to its defaults and silently wipes this. */
    if (shadeCtx) shadeCtx.imageSmoothingQuality = 'high';
    if (shadeCtx && !shadeEdges) {
      var W = cvs.width, H = cvs.height, B = Math.round(W * 200 / 2000), ctx = shadeCtx;
      var mk = function (ax, ay, bx, by) {
        var g = ctx.createLinearGradient(ax, ay, bx, by);
        [[0, 1], [0.15, 0.972], [0.3, 0.884], [0.5, 0.5],
         [0.7, 0.116], [0.85, 0.028], [1, 0]].forEach(function (st) {
          g.addColorStop(st[0], 'rgba(0,0,0,' + st[1] + ')');
        });
        return g;
      };
      shadeEdges = [
        { grad: mk(0, 0, B, 0), x: 0, y: 0, w: B, h: H },
        { grad: mk(W, 0, W - B, 0), x: W - B, y: 0, w: B, h: H },
        { grad: mk(0, 0, 0, B), x: 0, y: 0, w: W, h: B },
        { grad: mk(0, H, 0, H - B), x: 0, y: H - B, w: W, h: B }
      ];
    }
  }
  function shadePlace(cam) { placeComp(cvs, cam); }
  /* The comp square, at a camera, in the stage's own px: the shading canvas
     during a move, the Spin's video while it turns. */
  function placeComp(cvs, cam) {
    if (!cvs || !cam) return;
    var css = (COMP_SPAN / window.PP.CardStage.LAYOUT_SCALE) * cam.scale;
    cvs.style.display = 'block';
    cvs.style.width = css.toFixed(1) + 'px';
    cvs.style.height = css.toFixed(1) + 'px';
    var W = stage.clientWidth, H = stage.clientHeight;
    var l = W / 2 + cam.x - css / 2, t = H / 2 + cam.y - css / 2;
    cvs.style.left = l.toFixed(1) + 'px';
    cvs.style.top = t.toFixed(1) + 'px';
    /* ⛔ FADED OUT AT THE STAGE'S EDGE. .pp-viewport is `isolation: isolate`
       over its own backdrop, so the multiply only has something to multiply
       against inside the stage; past its edge the clip paints as plain pixels,
       and its white read as a lighter box round the stage mid-move (measured:
       #fff on a #fafbfc page, 18 Oct). Since v2e the shut card is framed
       bigger than the open one, so the comp square is wider than the stage for
       part of every move, which it never was when the camera was frozen on the
       open card. Only ground is out there, the card's cast shadow included,
       and a hard clip cut that shadow off square; a 48 px fade to the edge
       does not. */
    var F = 48;
    var cl = Math.max(0, -l), ct = Math.max(0, -t), cr = Math.max(0, l + css - W), cb = Math.max(0, t + css - H);
    var m = '';
    if (cl || ct || cr || cb) {
      var g = function (dir, a, b, len) {
        var a1 = a ? a + F : 0, b1 = b ? len - b - F : len;
        return 'linear-gradient(' + dir + ', transparent ' + a.toFixed(1) + 'px, #000 ' + a1.toFixed(1) + 'px, #000 ' +
          b1.toFixed(1) + 'px, transparent ' + (len - b).toFixed(1) + 'px)';
      };
      m = g('to right', cl, cr, css) + ', ' + g('to bottom', ct, cb, css);
    }
    if (cvs.getAttribute('data-mask') !== m) {
      cvs.setAttribute('data-mask', m);
      cvs.style.webkitMaskImage = cvs.style.maskImage = m;
      cvs.style.webkitMaskComposite = m ? 'source-in' : '';
      cvs.style.maskComposite = m ? 'intersect' : '';
    }
  }
  function shadeDraw(v) {
    if (!shadeCtx || !v || !v.videoWidth) return;
    var ctx = shadeCtx, W = cvs.width, H = cvs.height;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(v, 0, 0, W, H);
    ctx.globalCompositeOperation = 'destination-out';
    shadeEdges.forEach(function (e) { ctx.fillStyle = e.grad; ctx.fillRect(e.x, e.y, e.w, e.h); });
    ctx.globalCompositeOperation = 'source-over';
  }
  function shadeHide() {
    if (!cvs) return;
    /* A frame after the still is back, so there is never a frame with no
       shading at all (card-moves.js does the same). */
    requestAnimationFrame(function () { if (!moving) cvs.style.display = 'none'; });
  }

  /* ── ONE CLIP, FROM ONE POSE TO ANOTHER ─────────────────────────────────
     Plays `name` from geometry frame `from` to `to` on its axis, the planes
     posed from the video's own clock (requestVideoFrameCallback, with a timer
     under it: a video whose frame callbacks stop — a hidden or throttled tab —
     must still land), and the 2D camera eased from `c0` to `c1` by how far
     through the run the pose is, so the camera is a function of the pose and
     a reversal never jumps.

     ⛔ THE EASING GOES ON THE VIDEO'S RATE, NOT ON THE FRAME NUMBER. The pose
     is read from mediaTime, so slewing playbackRate eases the paper and the
     shading by the same amount in the same commit. Easing the frame number
     would put the card at a pose the shading never rendered. And never by
     seeking per frame: ~14fps on these encodes.

     NO CLIP, NO WAIT. If the clip is not decodable within `wait` ms (still
     downloading, no H.264 in the engine), the same run plays on a clock,
     unshaded — the card still moves where it was asked to go, at the same
     tempo, accumulating clamped deltas so a backgrounded tab resumes rather
     than skipping to the end. */
  var moving = false, moveTok = 0;
  function runClip(name, from, to, c0, c1, o) {
    o = o || {};
    var tok = moveTok;
    var axis = CLIP_AXIS[name], n = lastOf(axis), rev = !!CLIP_REVERSED[name];
    var span = Math.abs(to - from) || 1;
    var frameOf = function (t) {
      var i = Math.max(0, Math.min(n, Math.round(t * FPS)));
      return rev ? n - i : i;
    };
    var timeOf = function (g) { return ((rev ? n - g : g) + 0.1) / FPS; };
    var rateAt = function (g) { return o.rate ? o.rate(rev ? n - g : g) : 1; };
    var past = function (g) { return to >= from ? g >= to : g <= to; };
    var camAt = function (g) {
      var u = Math.max(0, Math.min(1, Math.abs(g - from) / span));
      var e = easeInOut(u);
      var f = { x: c0.x + (c1.x - c0.x) * e, y: c0.y + (c1.y - c0.y) * e,
                scale: c0.scale * Math.pow(c1.scale / c0.scale, e) };
      /* Never bigger than this frame's own card can be shown: the camera of
         the clip moves inside ours, and a mid-run pose can be wider than
         either end. Scaled about the stage's centre, which is what x and y
         are measured from. */
      var b = unionAt(geoOf(axis), g);
      if (b && stage.clientWidth && stage.clientHeight) {
        var own = Math.min(stage.clientWidth / b.w, stage.clientHeight / b.h) * 0.98;
        if (own < f.scale) { f.x *= own / f.scale; f.y *= own / f.scale; f.scale = own; }
      }
      return f;
    };

    return new Promise(function (resolve) {
      var done = false, last = null;
      var frame = function (g) {
        if (past(g)) g = to;
        if (g === last) return;
        last = g;
        posePos(axis, g);
        var cam = camAt(g);
        applyFit(cam);
        if (useVid) { shadePlace(cam); shadeDraw(v); }
      };
      /* A run left behind by a newer press stops touching anything: the new
         run may already be playing the same decoder from the same pose. */
      var finish = function () {
        if (done) return;
        done = true;
        clearInterval(guard);
        if (tok !== moveTok) { resolve(false); return; }
        if (v) { try { v.pause(); } catch (e) {} }
        frame(to);
        resolve(true);
      };
      var v = cvs ? clipVid(name) : null, useVid = false, guard = 0;

      /* On the clock: no clip, or not in time. */
      var playClock = function () {
        var lastT = 0, i = rev ? n - from : from, end = rev ? n - to : to;
        var dir = end >= i ? 1 : -1;
        useVid = false;
        if (cvs) cvs.style.display = 'none';
        frame(from);
        var step = function (now) {
          if (done) return;
          if (tok !== moveTok) { finish(); return; }
          if (!lastT) lastT = now;
          var dt = Math.min(now - lastT, 100);
          lastT = now;
          i += dir * (dt / 1000) * FPS * (o.rate ? o.rate(Math.round(i)) : 1);
          var g = rev ? n - Math.round(i) : Math.round(i);
          frame(g);
          if (past(g)) { finish(); return; }
          raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
      };

      /* On the video. */
      var playVid = function () {
        useVid = true;
        shadeSize(Math.max(c0.scale, c1.scale));
        var seq = 0, lastMoved = Date.now(), lastT = -1;
        var schedule = function () {
          var s = ++seq;
          if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(function (now, meta) { if (s === seq) step(meta); });
          else requestAnimationFrame(function () { if (s === seq) step(null); });
          setTimeout(function () { if (s === seq) step(null); }, 250);
        };
        var step = function (meta) {
          seq++;
          if (done) return;
          if (tok !== moveTok) { finish(); return; }
          var t = meta ? meta.mediaTime : v.currentTime;
          var g = frameOf(t);
          frame(g);
          var r = rateAt(g);
          if (Math.abs(v.playbackRate - r) > 0.02) { try { v.playbackRate = r; } catch (e) { /* 1x is fine */ } }
          if (past(g) || v.ended) { finish(); return; }
          schedule();
        };
        /* A clip that stops advancing must not leave the card half-moved for
           ever: it lands where it was going. */
        guard = setInterval(function () {
          if (done) { clearInterval(guard); return; }
          if (v.currentTime !== lastT) { lastT = v.currentTime; lastMoved = Date.now(); return; }
          if (Date.now() - lastMoved > 1500) finish();
        }, 250);
        v.addEventListener('ended', function () { if (!done) { frame(to); finish(); } }, { once: true });
        try { v.playbackRate = rateAt(from); } catch (e) {}
        frame(from);
        var pr = v.play();
        if (pr && pr.catch) pr.catch(function () {
          /* Refused (power saving, a background tab): the same run on the clock. */
          if (done) return;
          clearInterval(guard);
          seq++;
          useVid = false;
          if (cvs) cvs.style.display = 'none';
          playClock();
        });
        schedule();
      };

      /* Ready = decodable to the end (a blob is, the moment it is set) and
         sitting on the run's first frame. */
      var start = function () {
        if (done) return;
        if (tok !== moveTok) { finish(); return; }
        var t0 = timeOf(from);
        if (Math.abs(v.currentTime - t0) > 0.4 / FPS) {
          try { v.currentTime = t0; } catch (e) { /* not seekable yet */ }
        }
        var go = function () {
          if (v.seeking) return;
          v.removeEventListener('seeked', go);
          if (done) return;
          if (tok !== moveTok) { finish(); return; }
          playVid();
        };
        if (v.seeking) v.addEventListener('seeked', go);
        else go();
      };
      if (!v || !v.canPlayType || !v.canPlayType('video/mp4')) { playClock(); return; }
      if (v.readyState >= 4) { start(); return; }
      var started = false;
      var ready = function () {
        if (started || v.readyState < 4) return;
        started = true;
        clearTimeout(bail);
        v.removeEventListener('canplaythrough', ready);
        v.removeEventListener('loadeddata', ready);
        start();
      };
      v.addEventListener('canplaythrough', ready);
      v.addEventListener('loadeddata', ready);
      var bail = setTimeout(function () {
        if (started) return;
        started = true;
        v.removeEventListener('canplaythrough', ready);
        v.removeEventListener('loadeddata', ready);
        playClock();
      }, o.wait || SHADE_WAIT_MS);
    });
  }

  /* ── THE PAGE'S VIEWS: open, closed, back ────────────────────────────────
     A move is a plan of clips from the pose on screen to the view's rest pose
     (rd-card3d.js vwPlan, without its fade): closing runs `close` (or `rev`
     from a turned card), opening runs `open` (after `rev` from a turned card),
     turning over runs `close` and then `back`. A press mid-move plans again
     from the frame on screen — the other clip, seeked to the same pose — so a
     reversal never jumps. Every press bumps the token, so a fast run of
     presses lands on the last one. */
  var restView = 'closed';
  /* The page's toggles at the old fold's pace. FOLD_MS 900 + STAND_MS 620 was
     the whole close, 1.52 s; card-close is 101 frames, 3.37 s at 1x, so 2.2x
     is the same 1.5 s. The turn over is 60 frames: 1.6x makes it 1.25 s. */
  var MAIN_RATE = 2.2, BACK_RATE = 1.6;

  function planTo(p, view) {
    var nB = lastOf('back'), out = [];
    var shut = (p.axis === 'main' && p.g === FRAME_CLOSED) || (p.axis === 'back' && p.g === 0);
    if (view === 'closed') {
      if (!shut) out.push(p.axis === 'back' ? ['rev', p.g, 0] : ['close', p.g, FRAME_CLOSED]);
    } else if (view === 'open') {
      if (p.axis === 'back' && !shut) out.push(['rev', p.g, 0]);
      if (p.axis === 'back' || p.g > FRAME_OPEN) out.push(['open', p.axis === 'back' ? FRAME_CLOSED : p.g, FRAME_OPEN]);
    } else if (view === 'back') {
      if (p.axis === 'main' && !shut) out.push(['close', p.g, FRAME_CLOSED]);
      if (p.axis === 'main' || p.g < nB) out.push(['back', p.axis === 'back' ? p.g : 0, nB]);
    }
    return out;
  }

  /* At rest in `view`: its pose, its framing, its still; the canvas goes and
     the shadows come back. */
  function rested(view) {
    if (view === 'spin') { spinRest(); return; }
    spinOff();
    moving = false;
    restView = view;
    foldClosed = view !== 'open';
    var p = restPos(view);
    posePos(p.axis, p.g);
    setShade(view);
    applyFit(viewFit(view));
    stage.classList.remove(OPENING_CLASS);
    shadeHide();
    if (foldBtn) foldBtn.setAttribute('aria-checked', view === 'open' ? 'true' : 'false');
    root.setAttribute('data-pp-view-now', view);
    tagUpdate();
    emit('pp:share-view', { view: view });
  }

  function camNow() { return { x: camX, y: camY, scale: camScale }; }

  function moveTo(view) {
    if (!geom || !openBox) return;
    /* Into the Spin, or out of it: a fade, never a clip (its pose does not
       chain with the others). */
    /* And mid-fade too: the card is half gone, and a clip from there would
       leave it so (a press during a fade into the Spin, measured 18 Oct). */
    if (view === 'spin' || pos.axis === 'spin' || (zoom && zoom.style.opacity !== '')) { fadeTo(view); return; }
    if (view === 'back' && !backGeom) {
      loadBackGeom().then(function () { moveTo(view); }, function () { /* no turn over: stay */ });
      return;
    }
    var tok = ++moveTok;
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    zoomDrop();
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var segs = planTo(pos, view);
    if (!segs.length || reduced) { rested(view); return; }
    moving = true;
    restView = view;
    foldClosed = true;           /* no zoom until it is open and at rest */
    if (foldBtn) foldBtn.setAttribute('aria-checked', view === 'open' ? 'true' : 'false');
    tagUpdate();
    stage.classList.add(OPENING_CLASS);
    var c1 = viewFit(view);
    if (!c1) { rested(view); return; }
    var next = function (i, c0) {
      if (tok !== moveTok) return;
      if (i >= segs.length) { rested(view); return; }
      var s = segs[i];
      var end = restOf({ axis: CLIP_AXIS[s[0]], g: s[2] });
      var cEnd = i === segs.length - 1 ? c1 : (viewFit(end) || c1);
      runClip(s[0], s[1], s[2], c0, cEnd, {
        rate: function () { return CLIP_AXIS[s[0]] === 'back' ? BACK_RATE : MAIN_RATE; },
        wait: 700
      }).then(function (ok) { if (ok) next(i + 1, cEnd); });
    };
    next(0, camNow());
  }


  /* ── THE SPIN (18 Oct; Daniel: "Default should be the spin, then closed, open,
     and back") ──────────────────────────────────────────────────────────────
     The recipient's own card, open and floating, turning a full 360 degrees in
     Yonatan's seamless 4 s loop: card-geometry-spin-float.json (120 frames)
     and card-spin-float(-sm).mp4, video frame i = geometry frame i, looped.
     The page opens on it, and it is the first of the views.

     ONE SCENE. The same eight planes every other view poses, posed from the
     spin's geometry through CardStage.apply(), so the photos, the note, the
     inside top and all the art (CoverBack included) are the ones already
     painted. apply() culls by `facing` and shows each leaf's paper back when
     its photo turns away, which is exactly what a turning card needs.

     THE SHADING IS THE VIDEO ITSELF, mix-blend-mode: multiply, as the design
     page's Spin (rd-card3d.js RDF3D.spin): a blend costs nothing per frame,
     where drawing a looping 2000 px clip into the canvas would read megapixels
     back 30 times a second for as long as the page is open. Safe for the same
     reason the canvas is: it is a sibling of .pp-zoom, inside the stage that
     isolates the blend over its own ground, placed by box and faded at the
     stage edge (placeComp). The planes follow the video's own clock
     (requestVideoFrameCallback), so art and shading are the same frame.

     .pp-viewport--opening stays on while it turns: no ground pool (the card is
     in the air), no drop-shadow re-rastered every frame, no still.

     FETCHED WHOLE (a Blob), as Yonatan's page and the design page do: a
     streamed loop stalls. The 1000 px copy by the same rule as the other
     clips. Reduced motion: frame 0, still, the clip paused on it. */
  var spin = { geom: null, vid: null, on: false, shaded: false, tok: 0, rv: 0, raf: 0, frame: 0, ready: null };
  var spinBox = null;
  if (!CLIPS.spin && cfg.spin) CLIPS.spin = cfg.spin;
  function spinUnion() {
    if (!spin.geom) return null;
    var boxes = [];
    for (var f = 0; f <= lastOf('spin'); f += 3) {
      var t = window.PP.CardStage.frameTransforms(spin.geom, f);
      Object.keys(t.planes).forEach(function (k) { boxes.push(t.planes[k].box); });
    }
    return union(boxes);
  }
  /* The geometry, the clip as a blob, and its first frame decoded. Resolves
     true when the shaded Spin can play, false when only the planes can. */
  function loadSpin() {
    if (spin.ready) return spin.ready;
    if (!CLIPS.spin || !cfg.spinGeom) return Promise.reject(new Error('no spin'));
    if (!spin.vid) {
      var v = spin.vid = document.createElement('video');
      v.className = 'pp-share__spinvid';
      ['muted', 'playsinline', 'loop', 'disablepictureinpicture', 'disableremoteplayback'].forEach(function (a) { v.setAttribute(a, ''); });
      v.setAttribute('aria-hidden', 'true');
      v.muted = true; v.loop = true; v.playsInline = true; v.preload = 'auto';
      v.style.cssText = 'position:absolute;z-index:5;mix-blend-mode:multiply;pointer-events:none;display:none;';
      stage.appendChild(v);
    }
    spin.ready = Promise.all([
      getJson(cfg.spinGeom).then(function (g) {
        if (!g || !g.camera || !g.planes) throw new Error('bad spin geometry');
        spin.geom = g;
        if (openBox) spinBox = spinUnion();
      }),
      clipBlob('spin')
    ]).then(function (r) {
      var v = spin.vid, src = r[1];
      if (!src) return false;
      if (v.getAttribute('src') !== src) { v.src = src; try { v.load(); } catch (e) {} }
      if (v.readyState >= 2) return true;
      return new Promise(function (done) {
        var t = setTimeout(function () { done(v.readyState >= 2); }, 8000);
        v.addEventListener('loadeddata', function () { clearTimeout(t); done(true); }, { once: true });
        v.addEventListener('error', function () { clearTimeout(t); done(false); }, { once: true });
      });
    });
    spin.ready.catch(function () { spin.ready = null; });
    return spin.ready;
  }
  function spinPose(f) {
    spin.frame = f;
    posePos('spin', f);
  }
  /* The frame the clip is showing (or the last one posed), posed again: a
     resize or a fade back in lands on the picture under it. */
  function spinFrame() {
    var v = spin.vid, n = lastOf('spin') + 1;
    var f = (v && v.readyState >= 2) ? ((Math.round(v.currentTime * FPS) % n) + n) % n : spin.frame;
    spinPose(f);
  }
  function spinStopLoop() {
    spin.tok++;
    if (spin.rv && spin.vid && spin.vid.cancelVideoFrameCallback) spin.vid.cancelVideoFrameCallback(spin.rv);
    cancelAnimationFrame(spin.raf);
    spin.rv = 0; spin.raf = 0;
  }
  function spinReduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  /* Turning: the planes on the video's clock. Without a usable clip (refused,
     not decodable) it turns unshaded on the clock of the page, 30 frames a
     second, rather than standing still. Reduced motion: frame 0, still. */
  function spinStart() {
    spinStopLoop();
    var my = spin.tok, v = spin.vid, n = lastOf('spin') + 1;
    var shaded = spin.shaded = !!(v && v.readyState >= 2 && !(v.error));
    if (v) v.style.display = 'none';
    if (shaded) placeComp(v, camNow());
    if (spinReduced()) {
      if (v) { try { v.pause(); if (v.currentTime) v.currentTime = 0; } catch (e) {} }
      spinPose(0);
      return;
    }
    var clock = function () {
      spin.shaded = false;
      if (v) v.style.display = 'none';
      var t0 = null, f0 = spin.frame;
      var step = function (now) {
        if (my !== spin.tok) return;
        if (t0 === null) t0 = now;
        spinPose((f0 + Math.floor((now - t0) / 1000 * FPS)) % n);
        spin.raf = requestAnimationFrame(step);
      };
      spin.raf = requestAnimationFrame(step);
    };
    if (!shaded) { clock(); return; }
    var next = function () {
      if (my !== spin.tok) return;
      if (v.requestVideoFrameCallback) {
        spin.rv = v.requestVideoFrameCallback(function (now, meta) {
          if (my !== spin.tok) return;
          spinPose(((Math.round(meta.mediaTime * FPS) % n) + n) % n);
          next();
        });
      } else {
        spin.raf = requestAnimationFrame(function () {
          if (my !== spin.tok) return;
          spinPose(((Math.round(v.currentTime * FPS) % n) + n) % n);
          next();
        });
      }
    };
    try { v.playbackRate = 1; } catch (e) {}
    var pr = v.play();
    next();
    if (pr && pr.catch) pr.catch(function () { if (my === spin.tok) { spinStopLoop(); my = spin.tok; clock(); } });
  }
  /* Away from the Spin: the loop stops and the clip is hidden, kept loaded for
     coming back. */
  function spinOff() {
    if (!spin.on) return;
    spin.on = false;
    spinStopLoop();
    if (spin.vid) {
      try { spin.vid.pause(); } catch (e) {}
      spin.vid.style.display = 'none';
    }
  }
  /* At rest in the Spin. */
  function spinRest() {
    moving = false;
    restView = 'spin';
    foldClosed = true;          /* no zoom on a turning card */
    stage.classList.remove('pp-viewport--closed');
    stage.classList.add(OPENING_CLASS);
    shadeHide();
    spin.on = true;
    spinPose(spin.frame || 0);
    applyFit(viewFit('spin'));
    spinStart();
    if (foldBtn) foldBtn.setAttribute('aria-checked', 'false');
    root.setAttribute('data-pp-view-now', 'spin');
    tagUpdate();
    emit('pp:share-view', { view: 'spin' });
  }

  /* ── THE FADE, into or out of the Spin ──────────────────────────────────────
     The Spin's pose does not chain with Closed, Open and Back (it is a card in
     the air, mid-turn), so the change is a fade through the stage's ground:
     the card (and the clip's shading with it) fades out, the pose changes
     under it, and the new one fades in. Every press bumps the token, so a run
     of presses lands on the last one. Reduced motion: the change, no fade. */
  var FADE_OUT_MS = 180, FADE_IN_MS = 240;
  function fadeEls() { return [zoom, cvs, spin.vid].filter(Boolean); }
  function fadeTo(view) {
    var tok = ++moveTok;
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    zoomDrop();
    var go = function () {
      if (tok !== moveTok) return;
      if (view === 'back' && !backGeom) view = 'closed';
      rested(view);
    };
    var ready = view === 'spin' ? loadSpin().then(function () {}, function () {})
              : view === 'back' ? loadBackGeom().then(function () {}, function () {})
              : Promise.resolve();
    if (spinReduced()) { ready.then(go); return; }
    moving = true;
    restView = view;
    foldClosed = true;
    tagUpdate();
    fadeEls().forEach(function (el) { el.style.transition = 'opacity ' + FADE_OUT_MS + 'ms linear'; el.style.opacity = '0'; });
    var out = new Promise(function (r) { setTimeout(r, FADE_OUT_MS + 20); });
    Promise.all([out, ready]).then(function () {
      if (tok !== moveTok) return;
      go();
      requestAnimationFrame(function () {
        if (tok !== moveTok) return;
        fadeEls().forEach(function (el) { el.style.transition = 'opacity ' + FADE_IN_MS + 'ms linear'; el.style.opacity = ''; });
        setTimeout(function () {
          if (tok !== moveTok) return;
          fadeEls().forEach(function (el) { el.style.transition = ''; });
        }, FADE_IN_MS + 40);
      });
    });
  }

  /* ── the opening ─────────────────────────────────────────────────────────
     The card arrives SHUT, square to its cover and framed on it, holds a beat
     so the recipient sees the design the sender chose, then plays card-open:
     the planes posed from the clip, the clip's shading over them, the camera
     easing from the shut card's framing to the open card's. (Before v2e the
     shut card lay at the artist's angle and was stood up by hand, then tipped
     back down before the unfold; v2e's frame 100 is already straight down on
     the cover, so the stand and the tip are gone and the clip is the motion.)

     The builder's pacing, verbatim: openRate() on the video's rate, faster
     on a phone. */
  /* The beat on the shut card before it opens. It was 400 ms plus the 620 ms
     stand-and-tip; the tip is gone, so the hold keeps most of that time on the
     cover, which is the point of it. Shorter on a phone, as the unfold is
     (RATE_BOOST). */
  var ARRIVE_HOLD_MS = 700;

  function playOpening() {
    var c0 = viewFit('closed'), c1 = viewFit('open');
    if (!c0 || !c1) { rested('open'); wireControls(); watchFoot(); return; }
    var tok = ++moveTok;
    moving = true;
    restView = 'open';
    runClip('open', FRAME_CLOSED, FRAME_OPEN, c0, c1, {
      rate: function (idx) { return openRate(idx); },
      wait: SHADE_WAIT_MS
    }).then(function () {
      if (tok !== moveTok) return;
      settle();
    });
  }

  function settle(view) {
    raf = 0;
    rested(view || 'open');
    /* A resize during the opening was held back (see the resize handler). */
    var w = stage.clientWidth, h = stage.clientHeight;
    if (lastW && (Math.abs(w - lastW) >= lastW * 0.15 || Math.abs(h - lastH) >= lastH * 0.15)) refit();
    /* The card is at rest and openBox is computed: the zoom and the views can
       be offered now, and not a frame before. */
    wireControls();
    watchFoot();
  }

  /* ⭐ THE PAGE OPENS ON THE SPIN (Daniel, 18 Oct). The shut card at once, as
     the placeholder while the clip arrives, then a fade into the card turning.
     Without a Spin (no clip configured, no geometry) the arrival is the
     opening below, as it was. The clip gets SPIN_WAIT_MS; past that the Spin
     starts anyway and its shading joins when it can (spinStart's clock). */
  var SPIN_WAIT_MS = 6000;
  function arriveSpin() {
    posePos('main', FRAME_CLOSED);
    setShade('closed');
    applyFit(viewFit('closed') || fitFor(openBox, panelBox));
    if (!spinReduced()) stage.classList.remove(OPENING_CLASS);   /* the shut card with its still while it waits */
    reveal();
    var wait = new Promise(function (r) { setTimeout(r, SPIN_WAIT_MS); });
    var arrived = false;
    var land = function () {
      if (arrived) return;
      arrived = true;
      if (!spin.geom) { stage.classList.add(OPENING_CLASS); playOpening(); return; }
      var tok = ++moveTok;
      var first = function () {
        if (tok !== moveTok) return;
        settle('spin');
      };
      if (spinReduced()) { first(); return; }
      fadeEls().forEach(function (el) { el.style.transition = 'opacity ' + FADE_OUT_MS + 'ms linear'; el.style.opacity = '0'; });
      setTimeout(function () {
        first();
        requestAnimationFrame(function () {
          fadeEls().forEach(function (el) { el.style.transition = 'opacity ' + FADE_IN_MS + 'ms linear'; el.style.opacity = ''; });
          setTimeout(function () { fadeEls().forEach(function (el) { el.style.transition = ''; }); }, FADE_IN_MS + 40);
        });
      }, FADE_OUT_MS + 20);
    };
    Promise.race([loadSpin().then(function () {}, function () {}), wait]).then(land);
  }

  function play() {
    if (CLIPS.spin && cfg.spinGeom) { arriveSpin(); return; }
    var reduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      /* No clip: the open card at rest. Someone who asked for less motion still
         gets to look closely and to close or turn the card; the animations
         they declined are the card's, not the page's. */
      rested('open');
      reveal();
      wireControls();
      watchFoot();
      return;
    }
    /* The shut card, standing square and framed on its cover, for a beat. */
    posePos('main', FRAME_CLOSED);
    setShade('closed');
    applyFit(viewFit('closed') || fitFor(openBox, panelBox));
    reveal();
    setTimeout(playOpening, PHONE ? Math.round(ARRIVE_HOLD_MS / RATE_BOOST) : ARRIVE_HOLD_MS);
  }

  function reveal() {
    if (zoom) zoom.classList.add('is-ready');
    root.classList.add('is-ready');
    say('');
    /* ⭐ THE FIRST MOMENT THE NOTE BOX HAS A HEIGHT. Everything before this is
       measured against an opening stage — see the bail in paintNote(). */
    try { paintNote(); } catch (e) { /* the note is already on the page */ }
    /* …and from here the box speaks for itself. Armed after that first fit so
       the observer's own initial callback is not a third caller doing the same
       work; every firing after this one is a real change. */
    try { watchNoteBox(); } catch (e) { /* the timer path still stands */ }
  }

  /* ── "Show this to someone else" ────────────────────────────────────────
     Daniel, 2026-09-10: the secondary button copies the link, and "the button
     itself changes to link copied with a little confetti animation. Not a
     little ugly black thing."

     ⛔ THE ORDER REFERENCE TRAVELS WITH THE LINK. IT IS NOT OPTIONAL.
     This was written once to copy `?s=<token>` alone, on the reasoning that
     `&o=`/`&n=` are the buyer's provenance and no business of a stranger's.
     That reasoning is wrong, and the Worker settled it in 2026-09-01: a link
     is SELF-VERIFYING. It names its own order, and the token is checked
     against what the browser stapled to that order's line at add-to-cart.
     There is no stored mapping to fall back on — KV holds only hand-minted
     tokens — so a token on its own resolves to nothing.

     Measured, not assumed: every token on orders #1019, #1020 and #1021
     answers 404 bare and 200 with the reference. A "tidy" link is a dead one.

     handleShare in fulfilment/worker.js answers the privacy point directly:
     "The order id is not a secret and is not treated as one: knowing it buys
     nothing without the 22 characters, and a wrong token answers exactly like
     a wrong order."

     What IS stripped is everything else on the address bar — `preview_theme_id`
     above all, which would hand a stranger a preview cookie for an unpublished
     theme. The URL is rebuilt from known-good parts rather than edited. */
  function shareUrl() {
    var t = token();
    if (!t) return '';
    /* A mockup's link is artists.picturepops.com/c/<t> (2026-10-12, artists-worker src/share-og.js): it sends a
       person here and gives a link preview (Slack, iMessage, WhatsApp) the card's own title and picture, which this
       page cannot: Liquid has no query string, so its Open Graph tags cannot name the card. */
    if (card && card.mockup && /^[A-Za-z0-9]{22}$/.test(t)) return 'https://artists.picturepops.com/c/' + t + '?' + shareUtm();
    /* AN ORDER'S CARD TOO (2026-10-12, Daniel: "yes i want the unique card on each link"): the same address with the
       order reference on it, artists.picturepops.com/c/<t>?o=…&n=…, which sends a person back here with that query
       and gives a preview this card's cover and photographs (share-og.js resolves it through the same /s/ route). */
    if (/^[A-Za-z0-9]{22}$/.test(t)) return 'https://artists.picturepops.com/c/' + t + withUtm(orderRef());
    /* orderRef() returns '?o=…&n=…' or ''; `s` is already the first param. */
    return window.location.origin + window.location.pathname +
           '?s=' + t + orderRef().replace(/^\?/, '&') + '&' + shareUtm();
  }
  /* ⭐ UTMs ON THE LINK SENT ON (brain Phase 0, 2026-10-19; projects/brain/docs/2026-10-19-utm-scheme.md). Added
     after the token and the order reference, never instead of them: the path and `o`/`n` are what open the card.
     artists.picturepops.com/c/ carries every utm_* on to /pages/card (share-og.js storeShareLink). A link copied
     from a page that itself arrived tagged gets a fresh tag, because the URL is rebuilt, not edited. */
  function shareUtm() { return 'utm_source=share&utm_medium=referral&utm_content=share-page'; }
  function withUtm(q) { return (q ? q + '&' : '?') + shareUtm(); }

  /* Two paths, because neither is universal. navigator.clipboard needs a
     secure context AND, in Safari, a gesture it can still see — an await
     between the tap and the write loses it, so nothing is awaited before this.
     The execCommand fallback is deprecated and still the only thing that works
     in a WebView or over plain http, which is what a link pasted into a
     messaging app opens in.

     ⚠️ Returns a boolean rather than throwing: the caller has to know whether
     to claim success, and claiming it wrongly is the one unacceptable outcome.
     A button that says "Link copied" over an empty clipboard sends somebody to
     paste nothing into a message to their sister. */
  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        navigator.clipboard.writeText(text);
        return true;
      } catch (e) { /* fall through */ }
    }
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      /* Off-screen but NOT display:none or hidden — an unrendered element
         cannot be selected, and the copy silently does nothing. */
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      var ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return !!ok;
    } catch (e) {
      return false;
    }
  }

  function wireCopy() {
    var btn = root.querySelector('[data-pp-copy]');
    if (!btn) return;
    var url = shareUrl();
    if (!url) return;

    /* The button is `hidden` in the markup and only revealed here. There is no
       point offering to copy on a page whose token we could not even read. */
    btn.hidden = false;

    var idle = btn.querySelector('.pp-share__passlabel--idle');
    var done = btn.querySelector('.pp-share__passlabel--done');
    var timer = 0;

    btn.addEventListener('click', function () {
      if (!copyText(url)) {
        /* Say so rather than lying. The status line is already the page's
           voice for "something did not work". */
        say('Couldn’t copy — long-press the address bar to copy the link.', 'bad');
        return;
      }
      say('');

      /* ⛔ REMOVE, REFLOW, RE-ADD. A CSS animation does not restart when the
         class is already on the element, so a second press within the two
         seconds would swap the label and fire NO confetti. Reading offsetWidth
         between the two forces the style flush that makes the re-add count. */
      clearTimeout(timer);
      btn.classList.remove('is-copied');
      void btn.offsetWidth;
      btn.classList.add('is-copied');

      /* The label is what a screen reader is told, so the swap has to happen
         in the accessibility tree too — the CSS alone only moves pixels. */
      if (idle && done) {
        idle.setAttribute('aria-hidden', 'true');
        done.removeAttribute('aria-hidden');
      }

      timer = setTimeout(function () {
        btn.classList.remove('is-copied');
        if (idle && done) {
          idle.removeAttribute('aria-hidden');
          done.setAttribute('aria-hidden', 'true');
        }
      }, 2200);
    });
  }


  /* ══ LOOK CLOSE ══════════════════════════════════════════════════════════
     Daniel, 2026-09-10: the share page gets the preview's zoom and its
     dangling cursor label. (The Open/Closed toggle was asked for on the same
     day and withdrawn on the same day — see shared-card.liquid.)

     ⛔ WHY THIS IS A PORT AND NOT A SHARED MODULE. The builder's versions are
     Alpine methods reading `this.section`, `this.geom`, `camReady()` and a
     dock inset — none of which exist here. What IS shared is everything that
     matters to the eye: the same stage.css classes, the same ZOOM factor, the
     same reach maths, the same frame numbers. If one of those constants moves,
     move it in both. */

  var ZOOM = 2.3;                 /* configurator.js _previewGeom — keep equal */
  var zoomOn = false, panX = 0, panY = 0;
  var refitPending = false;
  var scrubT = 0;

  /* ── WHAT MADE THE DRAG FEEL WRONG ────────────────────────────────────────
     Daniel, 2026-09-10: "the scroll motion after zooming into the card is very
     laggy and unnatural. I need it to be natural like it is on an iPhone."

     Three separate causes, and all three had to go:

     1. ⛔ A FORCED LAYOUT PER MOVE. zoomGeom() called getBoundingClientRect()
        on the stage, so every pointermove flushed layout — while the finger was
        down and the compositor was already busy. The geometry cannot change
        mid-drag, so it is measured ONCE at pointerdown and reused.

     2. ⛔ THE VARIABLES WERE WRITTEN ON THE STAGE. A custom property set on an
        ancestor invalidates style for the whole subtree, and that subtree is
        eight planes, four photographs and the shading plate. Only .pp-zoom's
        transform reads --pp-cam-*, so during a drag they go on .pp-zoom
        itself and the invalidation is one element. They are removed again on
        exit so the fit written to the stage takes over.

     3. ⛔ NO COALESCING. A pointermove can fire several times per frame,
        especially at 120Hz, and each one wrote the camera — so the browser
        did work it would immediately throw away. Moves now record a target and
        one rAF writes it.

     And one thing that was simply missing: MOMENTUM. A native scroll keeps
     going when you let go, and resists rather than stops dead at the edge.
     Without that no amount of latency work would have felt right. */
  var FRICTION = 0.94;      /* per frame at 60Hz — decays to a stop in ~0.7s */
  var VMIN = 0.02;          /* px/ms below which a glide is over */
  var RUBBER = 0.35;        /* how far past the edge a finger may pull */
  var drag = null, dragMoved = false, dragRaf = 0, glideRaf = 0, zoomG = null;
  var exitRaf = 0, exitGuard = 0;
  /* ⚠️ THE POST-DRAG GUARD EXPIRES. A tap that merely ENDED a pan must not
     also zoom out, which is what dragMoved is for — but browsers disagree
     about whether a click follows a touch drag at all (preventDefault on the
     move can suppress it). If it never arrives the flag is still raised, and
     the next genuine tap gets eaten by a drag that finished minutes ago. The
     flag is therefore only honoured while it is fresh. */
  var dragEndedAt = 0;
  var DRAG_TAP_GRACE_MS = 320;

  function stageRect() { return stage.getBoundingClientRect(); }

  /* Where the card sits at ZOOM, and how far it may be roamed: its own
     projected overflow past the visible area, so every edge is reachable and
     no further. openBox already includes the shadow pool (see groundBox), so
     the reach accounts for it exactly as the fit does. */
  function zoomGeom() {
    var r = stageRect();
    if (!r.width || !r.height || !openBox) return null;
    var scaleZ = camScale * ZOOM;
    var halfW = (openBox.w * scaleZ) / 2;
    var halfH = (openBox.h * scaleZ) / 2;
    return {
      r: r,
      cx: camX * ZOOM, cy: camY * ZOOM, scaleZ: scaleZ,
      rangeX: Math.max(0, halfW - r.width / 2),
      rangeY: Math.max(0, halfH - r.height / 2)
    };
  }

  /* ⚠️ is-scrubbing IS THE "DO NOT EASE" FLAG, not a scrollbar thing.
     stage.css drops .pp-zoom's 520ms transform transition while it is set. The
     zoom-IN should ease; a roam or a drag must not, or the card lags the
     finger by half a second. */
  function camWrite(x, y, scale, smooth) {
    if (!smooth) {
      zoom.classList.add('is-scrubbing');
      clearTimeout(scrubT);
      scrubT = setTimeout(function () { zoom.classList.remove('is-scrubbing'); }, 140);
    }
    stage.style.setProperty('--pp-cam-x', x.toFixed(2) + 'px');
    stage.style.setProperty('--pp-cam-y', y.toFixed(2) + 'px');
    stage.style.setProperty('--pp-cam-scale', scale.toFixed(4));
  }

  /* The hot path — ONE property, and it is `transform` itself.
     Writing the three custom properties still made the engine resolve
     var() three times and rebuild the transform from them; writing the
     matrix directly skips all of that, and an inline transform simply
     shadows the rule in stage.css. Nothing else happens here: no class
     work, no timers, no measuring. Everything it needs was settled at
     pointerdown. */
  function camWriteHot(x, y, scale) {
    zoom.style.transform = 'translate3d(' + x.toFixed(2) + 'px,' +
                           y.toFixed(2) + 'px,0) scale(' + scale.toFixed(4) + ')';
  }

  function camClearHot() {
    zoom.style.transform = '';
    zoom.style.removeProperty('--pp-cam-x');
    zoom.style.removeProperty('--pp-cam-y');
    zoom.style.removeProperty('--pp-cam-scale');
  }

  /* Past the edge the card follows at a third of the finger, the way a native
     list does — it says "there is no more" without refusing to move, and the
     release springs it back. */
  function rubber(v, limit) {
    if (v > limit) return limit + (v - limit) * RUBBER;
    if (v < -limit) return -limit + (v + limit) * RUBBER;
    return v;
  }
  function clamp(v, limit) { return v < -limit ? -limit : v > limit ? limit : v; }

  /* ⚠️ THE GEOMETRY IS ONLY RE-MEASURED WHEN IT CAN HAVE CHANGED — on the way
     in, or if we have none. A roam re-used it and still called
     getBoundingClientRect() every mousemove, which is a forced layout in the
     middle of a gesture. */
  function zoomApply(px, py, smooth) {
    var g = (smooth || !zoomG) ? zoomGeom() : zoomG;
    if (!g) return;
    zoomG = g;
    panX = clamp(px, g.rangeX);
    panY = clamp(py, g.rangeY);
    if (smooth) {
      /* The eased move belongs to .pp-zoom's own transition, which reads the
         variables inherited from the stage — so the hot overrides have to go
         or they would pin the camera where the finger left it. */
      camClearHot();
      camWrite(g.cx + panX, g.cy + panY, g.scaleZ, true);
    } else {
      camWriteHot(g.cx + panX, g.cy + panY, g.scaleZ);
    }
  }

  /* One write per frame, whatever the digitiser's rate. */
  function dragFlush() {
    dragRaf = 0;
    if (!zoomG) return;
    camWriteHot(zoomG.cx + panX, zoomG.cy + panY, zoomG.scaleZ);
  }

  function glideStop() {
    if (glideRaf) { cancelAnimationFrame(glideRaf); glideRaf = 0; }
  }

  /* ── THE THROW ────────────────────────────────────────────────────────────
     What a finger leaving the glass is supposed to do. Friction is applied per
     MILLISECOND rather than per frame — FRICTION is quoted at 60Hz and raised
     to dt/16.667 — so a 120Hz phone and a 60Hz one decay at the same rate in
     real time instead of the fast one stopping twice as quickly.

     Past the edge the card is pulled back by a spring while the throw is still
     decaying, which is the same two forces a native list uses: it never stops
     dead, and it never sits somewhere it should not be. */
  function glideStart(g, vx, vy) {
    glideStop();
    if (!g) { zoom.classList.remove('is-scrubbing'); return; }
    var last = 0;
    var step = function (now) {
      if (!last) last = now;
      var dt = Math.min(now - last, 34);
      last = now;

      var k = Math.pow(FRICTION, dt / 16.667);
      vx *= k; vy *= k;
      panX += vx * dt;
      panY += vy * dt;

      var tx = clamp(panX, g.rangeX), ty = clamp(panY, g.rangeY);
      var out = tx !== panX || ty !== panY;
      if (out) {
        panX += (tx - panX) * 0.18;
        panY += (ty - panY) * 0.18;
        vx *= 0.6; vy *= 0.6;      /* the edge eats the throw */
      }
      camWriteHot(g.cx + panX, g.cy + panY, g.scaleZ);

      if (Math.abs(vx) > VMIN || Math.abs(vy) > VMIN ||
          Math.abs(panX - tx) > 0.5 || Math.abs(panY - ty) > 0.5) {
        glideRaf = requestAnimationFrame(step);
        return;
      }
      glideRaf = 0;
      panX = clamp(panX, g.rangeX);
      panY = clamp(panY, g.rangeY);
      camWriteHot(g.cx + panX, g.cy + panY, g.scaleZ);
      /* Only now does the card stop being "in motion" — dropping the promotion
         mid-throw is what the old 140ms timer did. */
      zoom.classList.remove('is-scrubbing');
    };
    glideRaf = requestAnimationFrame(step);
  }

  /* Desktop: the cursor's position over the stage IS the pan — cursor toward
     an edge reveals that edge. */
  function zoomFromCursor(mx, my, smooth) {
    var g = zoomGeom();
    if (!g) return;
    var tx = (mx - g.r.left) / g.r.width;
    var ty = (my - g.r.top) / g.r.height;
    tx = tx < 0 ? 0 : tx > 1 ? 1 : tx;
    ty = ty < 0 ? 0 : ty > 1 ? 1 : ty;
    zoomApply(g.rangeX * (1 - 2 * tx), g.rangeY * (1 - 2 * ty), smooth);
  }

  function coarse() {
    return window.matchMedia && window.matchMedia('(hover: none)').matches;
  }

  function canZoom() {
    /* Not while the card is shut or mid-fold: openBox is the framing and it is
       computed from the OPEN card, so zooming a folding one roams a box that
       is not there. */
    return !!openBox && !foldClosed && !moving && root.classList.contains('is-ready');
  }

  var EXIT_MS = 380;

  /* ⛔ ZOOMING OUT IS DRIVEN, NOT TRANSITIONED — and it has to be.
     Daniel, 2026-09-10: "Zoom out just snaps to the zoomed out state."

     It snapped because the exit changed three things in one go: it dropped the
     inline transform (so the element jumped back to the var-driven rule), it
     rewrote those variables, and on desktop it took `is-zoomed` off, which
     un-fixes the stage from a full-viewport overlay back into a 617px column.
     A CSS transition cannot smooth a LAYOUT change; the card is simply
     somewhere else on the next frame, and the transition has nothing to
     interpolate because its containing block moved out from under it.

     ⭐ SO THE TARGET IS EXPRESSED IN THE OVERLAY'S OWN COORDINATES FIRST.
     The column fit is measured by taking the class off for one forced layout,
     reading the box, and putting it back — then shifted by the difference
     between the two stage centres, which is exactly what makes the animated
     pose and the final pose land on the same pixels. The class comes off only
     at the end, when the two are identical and the swap is invisible.

     On mobile `is-zoomed` changes no geometry, so both centres coincide, the
     shift is zero and the same code is a plain ease. */
  function zoomExit() {
    if (!zoomOn) return;
    zoomOn = false;
    glideStop();
    drag = null;
    if (dragRaf) { cancelAnimationFrame(dragRaf); dragRaf = 0; }
    zoomG = null;
    stage.classList.remove('is-zoomed');
    void stage.offsetWidth;
    var colFit = fitFor(openBox, panelBox);
    var colRect = stage.getBoundingClientRect();
    stage.classList.add('is-zoomed');
    void stage.offsetWidth;
    var overRect = stage.getBoundingClientRect();

    var land = function () {
      exitRaf = 0;
      camClearHot();
      zoom.classList.remove('is-scrubbing');
      stage.classList.remove('is-zoomed');
      void stage.offsetWidth;
      applyFit(colFit || fitFor(openBox, panelBox));
      /* A resize arrived while we were zoomed and was deferred; the card is
         back at its fit now, so that fit has to be the CURRENT one. */
      if (refitPending) { refitPending = false; refit(); }
      tagUpdate();
    };

    if (!colFit) { land(); return; }

    /* Where the card is at this instant, in the overlay's coordinates. */
    var g = zoomGeom();
    var from = g
      ? { x: g.cx + panX, y: g.cy + panY, scale: g.scaleZ }
      : { x: camX, y: camY, scale: camScale };
    var to = {
      x: colFit.x + (colRect.left + colRect.width / 2) - (overRect.left + overRect.width / 2),
      y: colFit.y + (colRect.top + colRect.height / 2) - (overRect.top + overRect.height / 2),
      scale: colFit.scale
    };

    /* Driven by hand, so the inline transform stays in charge throughout and
       nothing hands over to the CSS rule until the poses match. */
    zoom.classList.add('is-scrubbing');
    var t0 = null;
    var step = function (now) {
      if (t0 === null) t0 = now;
      var u = Math.min(1, (now - t0) / EXIT_MS);
      var e = 1 - Math.pow(1 - u, 3);
      camWriteHot(from.x + (to.x - from.x) * e,
                  from.y + (to.y - from.y) * e,
                  from.scale + (to.scale - from.scale) * e);
      if (u < 1) { exitRaf = requestAnimationFrame(step); return; }
      land();
    };
    if (exitGuard) clearTimeout(exitGuard);
    exitRaf = requestAnimationFrame(step);
    /* rAF does not run in a background tab, and a zoom must not be able to
       strand the card mid-flight — the same guard the tip carries. */
    exitGuard = setTimeout(function () {
      if (!exitRaf) return;
      cancelAnimationFrame(exitRaf);
      land();
    }, EXIT_MS + 900);
  }

  function zoomToggle(mx, my) {
    if (zoomOn) { zoomExit(); return; }
    if (!canZoom()) return;
    zoomOn = true;
    /* A press during the ease back out takes over from it rather than fighting
       it — otherwise both would be writing the camera. */
    if (exitRaf) { cancelAnimationFrame(exitRaf); exitRaf = 0; }
    if (exitGuard) { clearTimeout(exitGuard); exitGuard = 0; }
    /* ⛔ CLASS FIRST, THEN RE-FIT, THEN ZOOM. On desktop this class turns the
       stage into a full-viewport overlay, so every number the zoom is about to
       derive — the stage's box, the base fit, the reach — belongs to the NEW
       box. Re-fitting after the class and before zoomGeom() is what keeps them
       in step; reading offsetWidth in between forces the layout that makes it
       true rather than merely scheduled. */
    stage.classList.add('is-zoomed');
    void stage.offsetWidth;
    writeCam(openBox);
    zoomG = null;
    hintRetire();
    if (coarse()) {
      zoomApply(0, 0, true);          /* touch zooms in centred, then drags */
    } else {
      zoomFromCursor(mx, my, true);
    }
    tagUpdate();
  }

  /* ── the label on the cursor ───────────────────────────────────────────────
     A pill that hangs off the pointer. Its ATTACH POINT is the top-left
     corner — stage.css sets transform-origin: 0 0 and explains why: it swings
     like a pin on a string, so it has to rotate about the point nearest the
     cursor or the swing reads as a wobble.

     ⛔ NEVER A REACTIVE WRITE, AND NEVER A CSS TRANSITION ON THE POSITION.
     This runs on pointermove. The builder learned both the hard way: a
     reactive write per move re-runs every binding on the page and has drawn
     it blank, and a CSS transition fights the spring and smears it. Direct
     style writes inside one rAF, and stage.css transitions only the fade. */
  var tagEl = null, tagX = 0, tagY = 0, tagTX = 0, tagTY = 0, tagRot = 0, tagRaf = 0;

  function tagText() { return zoomOn ? 'Zoom out' : 'Click to zoom'; }

  /* ⛔ THE HINT RETIRES ITSELF. Daniel, 2026-09-10: "there's way too much
     violet purple on the page." A pill that tells you how to zoom has done its
     job the moment you zoom; leaving it on the card forever is decoration in
     the brand's loudest colour. It fades a few seconds after the first zoom
     and does not come back — the gesture is not something anybody forgets
     between one look and the next. */
  var hintDone = false, hintT = 0;
  function hintRetire() {
    if (hintDone) return;
    hintDone = true;
    clearTimeout(hintT);
    hintT = setTimeout(function () {
      var h = root.querySelector('[data-pp-zoomhint]');
      if (h) h.classList.add('is-done');
    }, 2600);
  }

  function tagUpdate() {
    var hint = root.querySelector('[data-pp-zoomhint]');
    if (hint) {
      hint.textContent = zoomOn ? 'Drag to move · tap to zoom out'
                                : 'Tap the card to zoom in';
      /* Shown from the moment the controls arm — which is the moment there is
         something to zoom. See the [hidden] override in shared-card.liquid:
         stage.css's (hover: none) rule would otherwise paint it empty from
         first load. */
      hint.hidden = !(canZoom() || zoomOn);
    }
    if (!tagEl) return;
    tagEl.textContent = tagText();
    /* The label is a HOVER affordance and a phone has no hover; stage.css
       hides it under (hover: none) as well, so this is belt and braces. */
    if (!zoomOn && !canZoom()) tagEl.classList.remove('is-on');
  }

  function tagSpring() {
    tagRaf = 0;
    /* A lag of ~0.22 is what makes it trail rather than stick to the pointer,
       and the rotation is taken from the horizontal lag, so it swings into the
       direction of travel and settles upright when the cursor stops. */
    var dx = tagTX - tagX, dy = tagTY - tagY;
    tagX += dx * 0.22;
    tagY += dy * 0.22;
    var want = Math.max(-14, Math.min(14, dx * 0.5));
    tagRot += (want - tagRot) * 0.18;
    tagEl.style.transform = 'translate3d(' + tagX.toFixed(1) + 'px,' + tagY.toFixed(1) +
                            'px,0) rotate(' + tagRot.toFixed(2) + 'deg)';
    if (Math.abs(dx) > 0.3 || Math.abs(dy) > 0.3 || Math.abs(want - tagRot) > 0.1) {
      tagRaf = requestAnimationFrame(tagSpring);
    }
  }

  function tagTo(mx, my) {
    if (!tagEl) return;
    var r = stageRect();
    /* Just below-right of the pointer, so the nub on the pill's top edge sits
       under the cursor rather than the cursor covering the text. */
    tagTX = mx - r.left + 16;
    tagTY = my - r.top + 20;
    if (!tagEl.classList.contains('is-on')) {
      /* Land it where the cursor is instead of flying in from the last place
         it was seen, which on a re-entry is across the whole stage. */
      tagX = tagTX; tagY = tagTY; tagRot = 0;
    }
    if (!tagRaf) tagRaf = requestAnimationFrame(tagSpring);
  }

  /* ── open / closed / back ─────────────────────────────────────────────────
     The page's Open / Closed / Back (share-page.liquid's row under the card,
     shared-card.liquid's toolbar) and the desktop fold switch all come here,
     through root.ppShare.view(). Since v2e each is Yonatan's own clip — see
     moveTo() and runClip() above — where it used to be the open frames run
     backwards unshaded, then the card stood up by hand, and Back was a flat
     image of the cover turned over in CSS over a hidden 3D card.

     ⛔ A ZOOM ENDS AT ONCE WHEN A MOVE STARTS. Its exit is a 380 ms hand-driven
     ease on .pp-zoom's inline transform, and the move writes the camera on
     every frame; both at once is two writers on one camera. Dropped, then
     moved: on a phone the zoomed stage is the same box, so the drop is only a
     scale change the move then carries on from. */
  var foldClosed = false, foldBtn = null;
  function easeInOut(u) {
    return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
  }
  function zoomDrop() {
    if (!zoomOn) return;
    zoomOn = false;
    glideStop();
    drag = null;
    if (dragRaf) { cancelAnimationFrame(dragRaf); dragRaf = 0; }
    if (exitRaf) { cancelAnimationFrame(exitRaf); exitRaf = 0; }
    if (exitGuard) { clearTimeout(exitGuard); exitGuard = 0; }
    zoomG = null;
    camClearHot();
    zoom.classList.remove('is-scrubbing');
    stage.classList.remove('is-zoomed');
    void stage.offsetWidth;
    applyFit(viewFit(restView));
    if (refitPending) { refitPending = false; refit(); }
  }

  /* A view asked for before the card is open and at rest (the toolbar is on
     screen during the opening on the classic page) is kept and played the
     moment it is. */
  var wantView = null;
  function view(v) {
    if (v !== 'open' && v !== 'closed' && v !== 'back' && v !== 'spin') return;
    if (!controlsWired) { wantView = v; return; }
    wantView = null;
    if (v === restView && !moving) return;
    moveTo(v);
  }

  var controlsWired = false;
  function wireControls() {
    /* ⛔ ARMED AT REST, NOT AT REVEAL. reveal() runs at the START of the
       opening — it is what makes the CLOSED card visible — so arming there
       would offer "Closed" and a zoom while the card is still unfolding, and
       zoom's framing is computed from the OPEN card. */
    if (controlsWired) return;
    controlsWired = true;
    emit('pp:share-open');
    tagEl = root.querySelector('[data-pp-zoomtag]');
    foldBtn = root.querySelector('[data-pp-fold]');
    if (foldBtn) {
      foldBtn.hidden = false;
      foldBtn.addEventListener('click', function (e) {
        e.stopPropagation();                 /* not also a zoom toggle */
        foldBtn.classList.add('animate');    /* no squash on the first paint */
        view(restView === 'open' ? 'closed' : 'open');
      });
    }
    /* Whatever the toolbar asked for while the card was still opening. */
    if (wantView && wantView !== restView) { var w0 = wantView; wantView = null; setTimeout(function () { view(w0); }, 0); }
    else wantView = null;
    /* The turn over's geometry and every clip, now the card is open: the
       first press should never wait on the network. */
    setTimeout(function () {
      if (stage.getAttribute('data-pp-back')) loadBackGeom().catch(function () {});
      ['close', 'open', 'back', 'rev'].forEach(function (c) { if (CLIPS[c]) clipVid(c); });
      loadSpin().catch(function () {});
    }, 600);
    stage.classList.add('is-zoomable');


    /* ⚠️ ON THE STAGE, NOT ON .pp-zoom. Both .pp-zoom and .pp-scene carry
       pointer-events: none — stage.css explains that they are full-size boxes
       at z = 0 which would otherwise swallow every click meant for a plane. */
    stage.addEventListener('click', function (e) {
      if (foldBtn && foldBtn.contains(e.target)) return;
      if (dragMoved) {
        dragMoved = false;
        if (Date.now() - dragEndedAt < DRAG_TAP_GRACE_MS) return;
      }
      zoomToggle(e.clientX, e.clientY);
    });

    stage.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch') return;
      if (zoomOn) zoomFromCursor(e.clientX, e.clientY, false);
      if (!tagEl) return;
      if (!zoomOn && !canZoom()) { tagEl.classList.remove('is-on'); return; }
      tagTo(e.clientX, e.clientY);
      tagEl.classList.add('is-on');
    });

    stage.addEventListener('pointerleave', function () {
      if (tagEl) tagEl.classList.remove('is-on');
    });

    /* ── touch: grab and throw the zoomed card ──────────────────────────────
       Relative, not the desktop cursor-follow: the card moves WITH the finger
       (drag left reveals the right). A drag also cancels the tap, so it never
       zooms out mid-roam. */
    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'touch' || !zoomOn) return;
      glideStop();
      /* Re-measured here and only here: this is the one moment in a gesture
         when the stage's box can legitimately have changed since the last
         one, and the one moment when a forced layout costs nothing. */
      var g = zoomGeom();
      if (!g) return;
      zoomG = g;
      drag = {
        id: e.pointerId, g: g,
        x: e.clientX, y: e.clientY,
        px: panX, py: panY,
        lx: e.clientX, ly: e.clientY, lt: e.timeStamp || performance.now(),
        vx: 0, vy: 0
      };
      dragMoved = false;
      /* On for the WHOLE gesture. The debounced version in camWrite re-added
         and removed this class every 140ms mid-drag, and the class carries
         will-change: transform — so the card was being promoted and demoted
         while it moved, which is its own kind of stutter. */
      zoom.classList.add('is-scrubbing');
      if (stage.setPointerCapture) {
        try { stage.setPointerCapture(e.pointerId); } catch (err) {}
      }
    });

    /* ⚠️ NOT PASSIVE. touch-action: none is what actually claims the gesture,
       but a browser that has already begun scrolling needs the preventDefault
       too, and a passive listener is not allowed to call it. */
    stage.addEventListener('pointermove', function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      if (e.cancelable) e.preventDefault();

      var dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (!dragMoved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) dragMoved = true;

      /* Velocity in px/ms, smoothed across samples so one jittery reading
         cannot throw the card across the stage on release. */
      var t = e.timeStamp || performance.now();
      var dt = t - drag.lt;
      if (dt > 0) {
        var ivx = (e.clientX - drag.lx) / dt, ivy = (e.clientY - drag.ly) / dt;
        drag.vx = drag.vx * 0.7 + ivx * 0.3;
        drag.vy = drag.vy * 0.7 + ivy * 0.3;
        drag.lx = e.clientX; drag.ly = e.clientY; drag.lt = t;
      }

      panX = rubber(drag.px + dx, drag.g.rangeX);
      panY = rubber(drag.py + dy, drag.g.rangeY);
      /* Record and let one rAF write it — a 120Hz digitiser fires several
         moves per frame and each extra write is work thrown away. */
      if (!dragRaf) dragRaf = requestAnimationFrame(dragFlush);
    }, { passive: false });

    var end = function (e) {
      if (!drag || (e && e.pointerId !== drag.id)) return;
      var g = drag.g, vx = drag.vx, vy = drag.vy;
      drag = null;
      dragEndedAt = Date.now();
      if (dragRaf) { cancelAnimationFrame(dragRaf); dragRaf = 0; }
      glideStart(g, vx, vy);
    };
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointercancel', end);

    tagUpdate();
  }

  /* ── boot ──────────────────────────────────────────────────────────────── */

  var t = token();
  /* view(v): 'open', 'closed' or 'back', played with the clip between (see
     moveTo); the page hears pp:share-view { view } each time the card comes
     to rest. */
  root.ppShare = { shareUrl: shareUrl, copyText: copyText, view: view };
  if (!t) { say('This link is missing its code.', 'bad'); emit('pp:share-gone', { missing: true }); return; }
  if (!cfg.worker || !window.PP_GEOMETRY_URL) {
    say('This page is not configured.', 'bad');
    emit('pp:share-gone', {});
    return;
  }

  /* Wired before the card is fetched, on purpose. The button is part of the
     page's furniture, not part of the card, and the foot is on screen while
     the Worker is still answering — a control that appears late is a control
     people have already scrolled past. */
  wireCopy();
  /* The Spin's clip and geometry from the start: the page opens on it. */
  if (CLIPS.spin && cfg.spinGeom) loadSpin().catch(function () {});

  say('Opening…');

  /* ⛔ ARTISTS' PUBLISHED DESIGNS (artists.picturepops.com), so a shared card made from
     one shows its cover — and NEVER DROPPED (2026-10-06). This was a 3-second race that
     painted no art at all when it lost, and the catalogue then took 2 s at the Worker
     alone: Yonatan's two artist cards (#1030) opened with a blank Part A, no border and
     no Back button. Started with everything else; a slow answer holds the opening up to
     COVER_HOLD and otherwise lands in the open card. A failure still changes nothing
     else. */
  var artistCatalogue = getJson(window.PP_ARTIST_CATALOGUE_URL || 'https://artists.picturepops.com/catalogue.json')
    .catch(function () { return null; });

  Promise.all([
    getCard(cfg.worker.replace(/\/+$/, '') + '/s/' + t + orderRef()),
    getJson(window.PP_GEOMETRY_URL),
    window.PP_DESIGNS_URL ? getJson(window.PP_DESIGNS_URL).catch(function () { return null; })
                          : Promise.resolve(null),
  ]).then(function (res) {
    card = res[0] && res[0].card;
    geom = res[1];
    if (!card || !card.photos || !card.photos.length) throw new Error('no card');
    emit('pp:share-card', card);
    if (!window.PP || !window.PP.CardStage) throw new Error('no stage');

    /* ⛔ FONTS BEFORE THE NOTE. paintNote() shrinks the text until it fits by
       measuring scrollHeight, and the three faces have different metrics and
       different scales (0.80 and 0.78 against the hand's 1). Fitting first and
       restyling after would size the note against a face it is not set in —
       the same class of mistake as measuring before fonts.ready. */
    paintFonts();
    /* ⛔ BEFORE paintPhotos, and before the first pose. The well decides the
       element every photograph is stretched into, so it has to be right the
       first time a panel is painted — not corrected a frame later. */
    paintWell();
    paintPhotos();
    paintNote();
    /* The store's own designs are in designs.json, which is already here, so they
       paint now and wait for nothing. An artist's design is only in the artist
       catalogue: painted when that lands, whenever that is. */
    var own = (res[2] && res[2].designs) || [];
    /* A mockup carries its own art (pp-fulfilment checks every address is the mockup's own on
       artists.picturepops.com): it is in no catalogue, so it is its own design, first. */
    if (card.mockup && card.art && typeof card.art === 'object') own = [{ id: card.design, name: card.design, art: card.art }].concat(own);
    var cover = paintCover({ designs: own }) ? Promise.resolve() : artistCatalogue.then(function (j) {
      try { paintCover({ designs: own.concat((j && j.designs) || []) }); } catch (e) { /* the card stands without it */ }
    });

    /* ⛔ AND FIT AGAIN ONCE THE FACES HAVE ARRIVED. A @font-face with a
       unicode-range is not fetched until something needs a glyph from it, so
       the fit above almost certainly measured a FALLBACK — and the fallback's
       metrics are not the face's. The builder does exactly this
       (configurator.js: fonts.ready -> queueNoteFit), and it matters more here
       now that the note can be set in classic or modern, whose scales are 0.80
       and 0.78 against the hand's 1. paintNote() re-measures from scratch and
       rewrites --pp-note-fit on every call, so running it twice is idempotent,
       not cumulative. (This said "resets to 100%" — that was the crude fitter's
       inline font-size, which is gone; see paintNote.) */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { try { paintNote(); } catch (e) {} });
    }

    /* Wait for the panels before the first pose: a card that unfolds onto four
       empty wells and fills them in afterwards is a worse first impression
       than half a second of nothing.

       ⛔ NOT img.decode(). Measured 2026-08-31 on theme dev: it never resolves
       here, and the page hangs on "Opening…" for ever. The planes have no
       width or height until CardStage.apply() has run once, so at this point
       every <img> is inside a zero-sized, never-rendered box — and a decode
       for something the compositor has no reason to raster can simply not
       come back. `load` fires regardless of layout.

       And it is a RACE, not a wait. Four Cloudinary images on a bad phone
       connection must not be able to hold the card shut: after the cap the
       opening runs and the pictures land in it.

       THE COVER TOO (2026-10-06), since the closed card is its cover and that
       is the first thing anyone sees: its catalogue first, then its art with
       the photographs. One cap for the lot, COVER_HOLD, no longer than the old
       worst case (3 s for the catalogue, then 2.5 s for the panels). */
    return Promise.race([
      cover.then(function () {
        /* Not the back: it is only seen when the card turns over. */
        var imgs = Array.prototype.slice.call(stage.querySelectorAll('.pp-plane__img[src], .pp-plane__art[src]:not([data-pp-backart])'));
        return Promise.all(imgs.map(function (im) {
          if (im.complete && im.naturalWidth) return Promise.resolve();
          return new Promise(function (done) {
            im.addEventListener('load', done, { once: true });
            im.addEventListener('error', done, { once: true });
          });
        }));
      }),
      new Promise(function (r) { setTimeout(r, COVER_HOLD); }),
    ]);
  }).then(function () {
    reframe();
    play();
  }).catch(function (err) {
    /* Busy is not gone: the card exists and the Worker asked us to come back (getCard). */
    if (err && err.busy) {
      sayBusy();
      emit('pp:share-busy', {});
      if (window.console) console.warn('[shared-card] busy after retries');
      return;
    }
    /* One message for every failure. A public page must not say whether a
       token exists, and a stranger cannot act on the difference anyway. */
    say('This card is not available.', 'bad');
    emit('pp:share-gone', {});
    if (window.console) console.warn('[shared-card]', err && err.message);
  });

  /* Recalibrate on resize, but never mid-opening — the layout scale changing
     under a running animation moves the units the frozen camera was fitted in.
     ~15% hysteresis, the same rule calibrate() uses internally. */
  var lastW = 0, lastH = 0, tid = 0;
  /* Re-fit the camera to a stage that has changed size. Pulled out of the
     resize handler because a zoom has to be able to run it LATER — see below. */
  function refit() {
    reframe();
    /* reframe() has just fitted the OPEN card; the card is re-posed and
       re-framed for the view it is resting in. */
    if (restView === 'spin' && spin.geom) {
      spinFrame();
      restCamera();
    } else {
      var p = restPos(restView);
      posePos(p.axis, p.g);
      restCamera();
    }
    /* reframe() puts the opening class on so the camera SNAPS rather than
       gliding to the new framing. Nothing is animating here, so take it
       straight back off — left on, a single rotate would kill the ground pool
       and the card's drop-shadow for the rest of the visit. The Spin keeps it
       (no pool, no drop-shadow, no still under a turning card). */
    if (restView !== 'spin') stage.classList.remove(OPENING_CLASS);
  }

  window.addEventListener('resize', function () {
    /* Never mid-move or before the card is first at rest: the layout scale
       must not change under a running clip. A resize in that window is
       caught by the next one, since lastW is still the size it was framed at. */
    if (!geom || moving || !controlsWired) return;
    clearTimeout(tid);
    tid = setTimeout(function () {
      var w = stage.clientWidth, h = stage.clientHeight;
      if (Math.abs(w - lastW) < lastW * 0.15 && Math.abs(h - lastH) < lastH * 0.15) return;
      /* ⛔ A ZOOM SURVIVES A MOVING ADDRESS BAR. On iOS a scroll collapses
         Safari's chrome, which resizes the viewport by well over 15% of this
         stage's height — and re-fitting under a live zoom threw the card back
         out, which read as the page reloading itself mid-look (Daniel,
         2026-09-10: "if I try to drag up, it reloads the page"). touch-action:
         none now stops the scroll that caused it; this stops the rest of the
         class — a rotate, a keyboard, a window drag.

         ⚠️ DEFERRED, NOT DISCARDED. Skipping the re-fit outright would leave
         the camera fitted to the old size, so a rotate-while-zoomed would come
         back to a card framed for the other orientation. The work is owed and
         zoomExit() pays it. */
      if (zoomOn) { refitPending = true; return; }
      refit();
    }, 180);
  }, { passive: true });
})();
